import type { Clock, ClockSlot, PlanEntry, ChannelAlert, NotificationTarget, NotificationKind, MeldeEreignis, PlayoutSettings, PlayoutLogEntry, SessionUser, BrandingJson, Channel, ChannelWithEndpoints, OutputEndpoint, StreamingServer, ServerStatus, ChannelEngine, ChannelStreamSettings, EngineOverviewChannel, LiveIngest, SrtIngest, ChannelStream, EngineState, LibraryItem, LibraryItemWithExtras, Playlist, PlaylistWithItems, JingleWithLibrary, Moderator, Show, ShowWithRelations, Schedule, ScheduleWithBlocks, TaktBackfillRun, TeamMember } from "./types";

import type { BlockKind, FieldDef, PageBlock, SiteSettings } from "@webradiosuite/shared/pages";
import type { PostAction, PostStatus } from "@webradiosuite/shared/posts";
import type { EventAction, EventStatus } from "@webradiosuite/shared/events";
// h-ui: Hosting
import type { BetriebRadio, BetriebRadioDetail, BetriebNutzungZeile, Grenzen, MeinTarif, Nutzung, SupportFreigabe, SupportZugriff, Tarif } from "./types";
// h-domain: eigene Domains
import type { DomainListe, RadioDomain } from "./types";
// h-umzug
import type { Loeschnachweis, UmzugAuftrag } from "./types";

const ACCESS_KEY = "wbr_access";

export function getAccessToken(): string | null {
  // h-ui: Während eines Support-Zugriffs gilt in DIESEM Tab das Support-Token.
  // Das eigene Token des Betreibers bleibt unangetastet in localStorage –
  // andere Tabs arbeiten weiter in der eigenen Sitzung, und „Beenden“ muss
  // nur den Support-Eintrag wegwerfen.
  const support = supportSitzung();
  if (support) return support.token;
  return localStorage.getItem(ACCESS_KEY);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_KEY, token);
}

export function clearAccessToken(): void {
  localStorage.removeItem(ACCESS_KEY);
}

let refreshing: Promise<boolean> | null = null;

/** Access-Token mit dem Refresh-Cookie erneuern (nur ein Versuch gleichzeitig). */
function refreshAccessToken(): Promise<boolean> {
  refreshing ??= fetch("/api/auth/refresh", { method: "POST", credentials: "include" })
    .then(async (res) => {
      if (!res.ok) return false;
      const body = (await res.json()) as { access_token?: string };
      if (!body.access_token) return false;
      setAccessToken(body.access_token);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/**
 * Das kleine Cover eines Titels als Blob. Die Bilddateien liegen auf der
 * Hörerseite (/media), die unter der Suite-Adresse nicht erreichbar ist –
 * darum über die API mit Anmeldung, wie im Studio. null, wenn es keins gibt.
 */
export async function libraryCoverBlob(id: string): Promise<Blob | null> {
  const token = getAccessToken();
  const res = await fetch(`/api/library/${encodeURIComponent(id)}/cover`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    credentials: "include",
  });
  return res.ok ? res.blob() : null;
}

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const token = getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  // Access-Token abgelaufen (nach einer Stunde): still erneuern statt abzumelden.
  // h-ui: Support-Token abgelaufen oder beendet – nicht erneuern (der Refresh
  // gehört zur eigenen Sitzung), sondern zurück in die eigene Sitzung.
  if (res.status === 401 && supportSitzung()) {
    supportVerlassen("/betrieb/radios");
    throw new Error("unauthorized");
  }
  if (res.status === 401 && !retried && !path.startsWith("/auth/") && (await refreshAccessToken())) {
    return request<T>(path, init, true);
  }
  if (res.status === 401) {
    clearAccessToken();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // h-ui: Tarifgrenzen zentral in einen verständlichen Satz übersetzen.
    throw fehlerAusAntwort(res.status, text);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // f-auth: Bei eingeschalteter Zwei-Faktor-Anmeldung kommt statt des Tokens
  // eine Challenge; weiter geht es mit kontoApi.loginZweiFaktor.
  login(email: string, password: string): Promise<LoginAntwort> {
    return request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }).then((r) => {
      const res = r as LoginAntwort;
      if ("access_token" in res) setAccessToken(res.access_token);
      return res;
    });
  },
  register(input: { tenant_name: string; tenant_slug: string; email: string; password: string; display_name?: string }): Promise<{ tenant_id: string; user_id: string }> {
    return request("/auth/register", { method: "POST", body: JSON.stringify(input) });
  },
  logout(): Promise<{ ok: true }> {
    // h-ui: Im Support-Zugriff heißt „Abmelden“: Zugriff beenden, zurück in die
    // eigene Sitzung – sonst wäre das eigene Token weg und das Support-Token bliebe.
    if (supportSitzung()) return supportBeenden().then(() => ({ ok: true as const }));
    return request("/auth/logout", { method: "POST" }).then((r) => {
      clearAccessToken();
      return r as { ok: true };
    });
  },
  changePassword(current_password: string, new_password: string): Promise<{ ok: true }> {
    return request("/auth/password", { method: "POST", body: JSON.stringify({ current_password, new_password }) });
  },
  me(): Promise<{ user: SessionUser | null }> {
    return request("/auth/me");
  },
  getBranding(): Promise<{ branding: BrandingJson }> {
    return request("/tenants/me/branding");
  },
  updateBranding(branding: BrandingJson): Promise<{ branding: BrandingJson }> {
    return request("/tenants/me/branding", { method: "PUT", body: JSON.stringify(branding) });
  },
  health(): Promise<{ status: string; version: string; uptime: number }> {
    return request("/health");
  },
  // --- Team: Zugänge, Rollen, Kanalzugehörigkeit -------------------------
  listTeam(): Promise<TeamMember[]> {
    return request("/users");
  },
  listRoles(): Promise<Array<{ key: string; label: string }>> {
    return request("/users/roles");
  },
  createTeamMember(body: { email: string; display_name: string; role: string; password: string }): Promise<{ id: string }> {
    return request("/users", { method: "POST", body: JSON.stringify(body) });
  },
  updateTeamMember(id: string, body: { display_name?: string; role?: string; editorial_lead?: boolean }): Promise<{ ok: true }> {
    return request(`/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  resetTeamPassword(id: string, password: string): Promise<{ ok: true }> {
    return request(`/users/${id}/password`, { method: "PUT", body: JSON.stringify({ password }) });
  },
  deleteTeamMember(id: string): Promise<{ ok: true }> {
    return request(`/users/${id}`, { method: "DELETE" });
  },
  setChannelMember(channelId: string, userId: string, role: string): Promise<{ ok: true }> {
    return request(`/channels/${channelId}/members/${userId}`, { method: "PUT", body: JSON.stringify({ role }) });
  },
  removeChannelMember(channelId: string, userId: string): Promise<{ ok: true }> {
    return request(`/channels/${channelId}/members/${userId}`, { method: "DELETE" });
  },

  listChannels(): Promise<Channel[]> {
    return request("/channels");
  },
  getChannel(slug: string): Promise<ChannelWithEndpoints> {
    return request(`/channels/${slug}`);
  },
  createChannel(input: { name: string; slug: string; status?: string; delivery_modes?: Record<string, unknown> }): Promise<Channel> {
    return request("/channels", { method: "POST", body: JSON.stringify(input) });
  },
  updateChannel(id: string, input: { name?: string; status?: string; delivery_modes?: Record<string, unknown> }): Promise<Channel> {
    return request(`/channels/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteChannel(id: string): Promise<{ ok: true }> {
    return request(`/channels/${id}`, { method: "DELETE" });
  },
  listEndpoints(channelId: string): Promise<OutputEndpoint[]> {
    return request(`/channels/${channelId}/endpoints`);
  },
  createEndpoint(channelId: string, input: {
    kind: OutputEndpoint["kind"]; server_id?: string; codec: OutputEndpoint["codec"]; bitrate_kbps: number;
    mount?: string; sid?: number; hls_variants?: number[]; enabled?: boolean;
  }): Promise<OutputEndpoint> {
    return request(`/channels/${channelId}/endpoints`, { method: "POST", body: JSON.stringify(input) });
  },
  updateEndpoint(id: string, input: { codec?: OutputEndpoint["codec"]; bitrate_kbps?: number; mount?: string; hls_variants?: number[]; enabled?: boolean }): Promise<OutputEndpoint> {
    return request(`/endpoints/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteEndpoint(id: string): Promise<{ ok: true }> {
    return request(`/endpoints/${id}`, { method: "DELETE" });
  },
  renderLiquidsoap(channelId: string): Promise<{ script: string; active: boolean; engine: EngineState }> {
    return request(`/channels/${channelId}/liquidsoap-render`, { method: "POST", body: JSON.stringify({}) });
  },

  // ---- Streaming-Server und Sende-Engine ----
  listServers(): Promise<StreamingServer[]> {
    return request("/servers");
  },
  createServer(input: {
    kind: StreamingServer["kind"]; name: string; host: string; port: number; public_url: string;
    source_password: string; admin_user?: string; admin_password: string; max_listeners?: number;
  }): Promise<StreamingServer> {
    return request("/servers", { method: "POST", body: JSON.stringify(input) });
  },
  updateServer(id: string, input: Partial<Pick<StreamingServer, "name" | "host" | "port" | "public_url" | "source_password" | "admin_user" | "admin_password" | "max_listeners" | "settings">>): Promise<StreamingServer> {
    return request(`/servers/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteServer(id: string): Promise<{ ok: true }> {
    return request(`/servers/${id}`, { method: "DELETE" });
  },
  getServerStatus(id: string): Promise<ServerStatus> {
    return request(`/servers/${id}/status`);
  },
  getServerConfig(id: string): Promise<{ path: string; content: string }> {
    return request(`/servers/${id}/config`);
  },
  applyStreaming(): Promise<{ at: string; errors: Array<{ scope: string; message: string }> }> {
    return request("/servers/apply", { method: "POST", body: JSON.stringify({}) });
  },
  getEngineOverview(): Promise<{ last_reconcile: string | null; channels: EngineOverviewChannel[] }> {
    return request("/engine/overview");
  },
  getChannelEngine(slug: string): Promise<ChannelEngine> {
    return request(`/channels/${slug}/engine`);
  },
  getChannelEngineLog(slug: string): Promise<{ log: string; script: string }> {
    return request(`/channels/${slug}/engine/log`);
  },
  saveStreamSettings(slug: string, input: ChannelStreamSettings): Promise<ChannelStreamSettings> {
    return request(`/channels/${slug}/stream-settings`, { method: "PUT", body: JSON.stringify(input) });
  },
  renewLivePassword(slug: string): Promise<{ ingest: LiveIngest }> {
    return request(`/channels/${slug}/engine/live-password`, { method: "POST", body: JSON.stringify({}) });
  },
  setSrtEnabled(slug: string, enabled: boolean): Promise<{ srt: SrtIngest; restart: boolean }> {
    return request(`/channels/${slug}/engine/srt`, { method: "PUT", body: JSON.stringify({ enabled }) });
  },
  renewSrtPassphrase(slug: string): Promise<{ srt: SrtIngest; restart: boolean }> {
    return request(`/channels/${slug}/engine/srt-passphrase`, { method: "POST", body: JSON.stringify({}) });
  },
  skipTrack(slug: string): Promise<{ ok: true }> {
    return request(`/channels/${slug}/engine/skip`, { method: "POST", body: JSON.stringify({}) });
  },
  playJingleOnAir(slug: string, jingleId: string): Promise<{ ok: true }> {
    return request(`/channels/${slug}/engine/jingles/${jingleId}`, { method: "POST", body: JSON.stringify({}) });
  },
  // ---- Übergabe: den Sender an das nächste Studio weitergeben ----
  uebergabeStatus(slug: string): Promise<Uebergabe> {
    return request(`/channels/${slug}/uebergabe`);
  },
  uebergabeAnbieten(slug: string, an: boolean): Promise<{ angebot: { bis: string; von: string } | null }> {
    return request(`/channels/${slug}/uebergabe/anbieten`, { method: "POST", body: JSON.stringify({ an }) });
  },
  uebergeben(slug: string, an: "a" | "b", gemeinsam: number): Promise<{ ok: boolean; sendeplatz: "a" | "b"; gemeinsam: number }> {
    return request(`/channels/${slug}/uebergabe`, { method: "POST", body: JSON.stringify({ an, gemeinsam }) });
  },
  /** Für Tastenkürzel, die nur den Slot kennen. */
  async playJingleSlot(slug: string, slot: number): Promise<{ ok: true } | null> {
    const jingles = await api.listJingles(slug);
    const jingle = jingles.find((j) => j.slot_index === slot);
    return jingle ? api.playJingleOnAir(slug, jingle.id) : null;
  },

  // ---- Ersteinrichtung, Erste Schritte, Studio ----
  setupStatus(): Promise<{ needs_setup: boolean; code_required?: boolean }> {
    return request("/setup/status");
  },
  runSetup(input: { radio_name: string; display_name?: string; email: string; password: string; setup_code?: string }): Promise<{ access_token: string; tenant_slug: string; listen_page: string }> {
    return request("/setup", { method: "POST", body: JSON.stringify(input) }).then((r) => {
      const res = r as { access_token: string; tenant_slug: string; listen_page: string };
      setAccessToken(res.access_token);
      return res;
    });
  },
  /** Die Kacheln des Dashboards – der Server liefert nur, was die Rolle sehen darf. */
  getDashboard(): Promise<{ kacheln: DashboardKachel[] }> {
    return request("/dashboard");
  },
  getOnboarding(): Promise<{ steps: Array<{ id: string; done: boolean; label: string; link: string; external?: boolean }>; done: boolean; listen_page: string; music_count: number }> {
    return request("/onboarding");
  },
  createPairingCode(): Promise<{ code: string; expires_at: string; server_url: string }> {
    return request("/studio/pairing-codes", { method: "POST", body: JSON.stringify({}) });
  },
  listStudioDevices(): Promise<Array<{ id: string; name: string; platform: string | null; app_version: string | null; created_at: string; last_seen_at: string | null; user_email: string; user_name: string | null; mine: boolean }>> {
    return request("/studio/devices");
  },
  revokeStudioDevice(id: string): Promise<{ ok: true }> {
    return request(`/studio/devices/${id}`, { method: "DELETE" });
  },
  getStudioReleases(): Promise<{ version: string; date?: string; notes?: string; files: Record<string, { file: string; sha256: string; size: number; url: string }> } | undefined> {
    return request("/studio/releases");
  },

  // ---- Hörer, Wünsche, Statistik, Meldelisten ----
  getPublicSettings(slug: string): Promise<{ settings: { requests: "off" | "moderated" | "auto"; show_history: boolean }; listen_page: string; embed_url: string }> {
    return request(`/channels/${slug}/public-settings`);
  },
  savePublicSettings(slug: string, input: { requests?: "off" | "moderated" | "auto"; show_history?: boolean }): Promise<{ settings: { requests: "off" | "moderated" | "auto"; show_history: boolean } }> {
    return request(`/channels/${slug}/public-settings`, { method: "PUT", body: JSON.stringify(input) });
  },
  listRequests(slug: string): Promise<Array<{ id: string; title: string; artist: string | null; listener_name: string | null; message: string | null; status: "pending" | "accepted" | "rejected" | "played"; created_at: string; decided_at: string | null }>> {
    return request(`/channels/${slug}/requests`);
  },
  acceptRequest(id: string): Promise<{ ok: true }> {
    return request(`/requests/${id}/accept`, { method: "POST", body: JSON.stringify({}) });
  },
  rejectRequest(id: string): Promise<{ ok: true }> {
    return request(`/requests/${id}/reject`, { method: "POST", body: JSON.stringify({}) });
  },
  getMessagesOpen(slug: string): Promise<{ open: boolean; open_until: string | null; opened_by: string | null }> {
    return request(`/channels/${slug}/messages/open`);
  },
  setMessagesOpen(slug: string, open: boolean, minutes?: number): Promise<{ open: boolean; open_until: string | null }> {
    return request(`/channels/${slug}/messages/open`, { method: "PUT", body: JSON.stringify({ open, minutes }) });
  },
  /** Sprachnachricht als abspielbare Adresse (Blob, weil der Player keinen Anmelde-Header schicken kann). */
  async messageAudioUrl(id: string): Promise<string> {
    const res = await fetch(`/api/messages/${id}/audio`, { credentials: "include", headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` } });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    return URL.createObjectURL(await res.blob());
  },
  listMessages(slug: string): Promise<Array<{ id: string; kind: "text" | "voice"; listener_name: string; place: string | null; message: string; status: "new" | "read" | "aired"; duration_ms: number | null; broadcast_ok: boolean; created_at: string; handled_at: string | null; wall: "on" | "off" | null }>> {
    return request(`/channels/${slug}/messages`);
  },
  setMessageStatus(id: string, status: "read" | "aired" | "hidden"): Promise<{ ok: true }> {
    return request(`/messages/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
  },
  blockMessageSender(id: string): Promise<{ ok: true }> {
    return request(`/messages/${id}/block`, { method: "POST", body: JSON.stringify({}) });
  },
  // ---- Mitmach-Module ----
  listModules(): Promise<Array<{ id: string; label: string; hint: string; fields: FieldDef[]; blocks: string[]; studio: boolean; enabled: boolean; settings: Record<string, unknown> }>> {
    return request("/modules");
  },
  saveModule(id: string, input: { enabled?: boolean; settings?: Record<string, unknown> }): Promise<{ id: string; enabled: boolean; settings: Record<string, unknown> }> {
    return request(`/modules/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  getStatsOverview(slug: string, days: number): Promise<{
    channel: { name: string; slug: string };
    days: number;
    summary: { avg_listeners: number; peak_listeners: number; peak_at: string | null; listener_hours: number; hours_measured: number };
    timeline: Array<{ hour: string; avg: number; peak: number }>;
    week_grid: Array<Array<number | null>>;
    streams: Array<{ label: string; share: number }>;
    shows: Array<{ title: string; avg_listeners: number; hours: number }>;
    top_tracks: Array<{ title: string; artist: string | null; plays: number; avg_listeners: number | null }>;
  }> {
    return request(`/stats/overview?channel=${encodeURIComponent(slug)}&days=${days}`);
  },
  checkPlaylog(slug: string, month: string): Promise<{ music: number; live: number; no_composer: number; no_isrc: number; no_label: number; ads: number; missing_details: Array<{ id: string; title: string; artist: string | null; plays: number }> }> {
    return request(`/reports/playlog/check?channel=${encodeURIComponent(slug)}&month=${month}`);
  },
  /** Lädt eine CSV mit Anmeldung herunter und speichert sie im Browser. */
  async downloadPlaylog(slug: string, month: string, format: "gema" | "gvl" | "summary" | "ads"): Promise<void> {
    const res = await fetch(`/api/reports/playlog.csv?channel=${encodeURIComponent(slug)}&month=${month}&format=${format}`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `meldeliste-${month}.csv`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // ---- Stundenuhren und Sendeablauf ----
  listClocks(): Promise<Clock[]> {
    return request("/clocks");
  },
  createClock(input: { name: string; color?: string; slots: ClockSlot[] }): Promise<{ id: string }> {
    return request("/clocks", { method: "POST", body: JSON.stringify(input) });
  },
  updateClock(id: string, input: { name: string; color?: string; slots: ClockSlot[] }): Promise<{ ok: true }> {
    return request(`/clocks/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  deleteClock(id: string): Promise<{ ok: true }> {
    return request(`/clocks/${id}`, { method: "DELETE" });
  },
  setDefaultClock(slug: string, clockId: string | null): Promise<{ ok: true }> {
    return request(`/channels/${slug}/default-clock`, { method: "PUT", body: JSON.stringify({ clock_id: clockId }) });
  },
  getPlan(slug: string, hours = 3): Promise<{ default_clock: { id: string; name: string } | null; entries: PlanEntry[] }> {
    return request(`/channels/${slug}/plan?hours=${hours}`);
  },
  insertPlanEntry(slug: string, libraryItemId: string, afterId: string | null): Promise<{ id: string }> {
    return request(`/channels/${slug}/plan/insert`, { method: "POST", body: JSON.stringify({ library_item_id: libraryItemId, after_id: afterId }) });
  },
  movePlanEntry(id: string, afterId: string | null): Promise<{ ok: true }> {
    return request(`/plan-entries/${id}/move`, { method: "POST", body: JSON.stringify({ after_id: afterId }) });
  },
  /** Moderationstext am Ablaufeintrag setzen; leerer Text löscht ihn. */
  setPlanText(id: string, text: string): Promise<{ ok: true }> {
    return request(`/plan-entries/${id}/text`, { method: "PUT", body: JSON.stringify({ text }) });
  },
  removePlanEntry(id: string): Promise<{ ok: true }> {
    return request(`/plan-entries/${id}`, { method: "DELETE" });
  },
  replan(slug: string): Promise<{ ok: true; planned: number }> {
    return request(`/channels/${slug}/plan/replan`, { method: "POST", body: JSON.stringify({}) });
  },
  uploadVoicetrack(slug: string, file: File, afterId: string | null, title?: string): Promise<{ id: string }> {
    const form = new FormData();
    form.append("file", file);
    if (afterId) form.append("after_id", afterId);
    if (title) form.append("title", title);
    return fetch(`/api/channels/${slug}/plan/voicetrack`, {
      method: "POST", body: form, credentials: "include", headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    }).then(async (res) => {
      if (!res.ok) throw fehlerAusAntwort(res.status, await res.text().catch(() => "")); // h-ui
      return (await res.json()) as { id: string };
    });
  },

  // ---- Störungen, Benachrichtigungen, Sendeablauf ----
  getAlerts(): Promise<{ open: ChannelAlert[]; recent: ChannelAlert[] }> {
    return request("/alerts");
  },
  listNotificationTargets(): Promise<{
    email_available: boolean;
    ereignisse: Record<MeldeEreignis, string>;
    targets: NotificationTarget[];
  }> {
    return request("/notification-targets");
  },
  createNotificationTarget(input: { kind: NotificationKind; name: string; config: Record<string, string>; ereignisse?: MeldeEreignis[] }): Promise<NotificationTarget> {
    return request("/notification-targets", { method: "POST", body: JSON.stringify(input) });
  },
  updateNotificationTarget(id: string, input: { name?: string; enabled?: boolean; config?: Record<string, string>; ereignisse?: MeldeEreignis[] }): Promise<NotificationTarget> {
    return request(`/notification-targets/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteNotificationTarget(id: string): Promise<{ ok: true }> {
    return request(`/notification-targets/${id}`, { method: "DELETE" });
  },
  testNotificationTarget(id: string): Promise<{ ok: true }> {
    return request(`/notification-targets/${id}/test`, { method: "POST", body: JSON.stringify({}) });
  },
  getPlayoutSettings(slug: string): Promise<{ settings: PlayoutSettings; defaults: PlayoutSettings }> {
    return request(`/channels/${slug}/playout-settings`);
  },
  savePlayoutSettings(slug: string, input: Partial<PlayoutSettings>): Promise<{ settings: PlayoutSettings; defaults: PlayoutSettings }> {
    return request(`/channels/${slug}/playout-settings`, { method: "PUT", body: JSON.stringify(input) });
  },
  getHistory(slug: string, limit = 30): Promise<PlayoutLogEntry[]> {
    return request(`/channels/${slug}/history?limit=${limit}`);
  },

  listLibrary(
    q: {
      q?: string;
      tag?: string;
      artist?: string;
      category?: string;
      /** Tempobereich in Schlägen je Minute. */
      bpm_min?: number;
      bpm_max?: number;
      /** Camelot-Kürzel, z. B. "8A". */
      camelot?: string;
      /** Nur Titel ohne Tempowert – für die Lückensuche. */
      ohne_bpm?: boolean;
      sort?: "neu" | "titel" | "interpret" | "bpm" | "bpm_ab" | "tonart";
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<LibraryItem[]> {
    const params = new URLSearchParams();
    if (q.q) params.set("q", q.q);
    if (q.category) params.set("category", q.category);
    if (q.tag) params.set("tag", q.tag);
    if (q.artist) params.set("artist", q.artist);
    if (q.bpm_min !== undefined) params.set("bpm_min", String(q.bpm_min));
    if (q.bpm_max !== undefined) params.set("bpm_max", String(q.bpm_max));
    if (q.camelot) params.set("camelot", q.camelot);
    if (q.ohne_bpm) params.set("ohne_bpm", "true");
    if (q.sort && q.sort !== "neu") params.set("sort", q.sort);
    if (q.limit) params.set("limit", String(q.limit));
    if (q.offset) params.set("offset", String(q.offset));
    const qs = params.toString();
    return request(`/library${qs ? `?${qs}` : ""}`);
  },
  getLibraryItem(id: string): Promise<LibraryItemWithExtras> {
    return request(`/library/${id}`);
  },
  uploadLibrary(file: File, meta: { title?: string; artist?: string; album?: string; track_no?: number ; relpath?: string; cover?: File}): Promise<LibraryItem> {
    const form = new FormData();
    form.append("file", file);
    if (meta.title) form.append("title", meta.title);
    if (meta.artist) form.append("artist", meta.artist);
    if (meta.album) form.append("album", meta.album);
    if (meta.track_no !== undefined && meta.track_no !== null) form.append("track_no", String(meta.track_no));
    // Der Weg beim Absender: daraus liest der Server die Scheibennummer
    // ("CD 1 von 2"). Ohne ihn ist die Datei auf dem Server heimatlos.
    if (meta.relpath) form.append("relpath", meta.relpath);
    // Das Bild aus demselben Ordner - bei Alben ohne eingebettetes Cover
    // die einzige Quelle.
    if (meta.cover) form.append("cover", meta.cover, meta.cover.name);
    return fetch("/api/library/upload", {
      method: "POST",
      body: form,
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    }).then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw fehlerAusAntwort(res.status, text); // h-ui: Mediathek-Grenze verständlich
      }
      return (await res.json()) as LibraryItem;
    });
  },
  updateLibraryItem(id: string, input: Record<string, unknown>): Promise<LibraryItemWithExtras> {
    return request(`/library/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteLibraryItem(id: string): Promise<{ ok: true }> {
    return request(`/library/${id}`, { method: "DELETE" });
  },
  streamUrl(id: string): string {
    return `/api/library/${id}/stream`;
  },
  reanalyze(id: string): Promise<{ job_id: string }> {
    return request(`/library/${id}/reanalyze`, { method: "POST", body: JSON.stringify({}) });
  },
  rightsCheck(id: string): Promise<{ item_id: string; allowed: boolean; reasons: string[]; entries: Array<{ territory: string; type: string }> }> {
    return request(`/library/${id}/rights-check`);
  },
  rightsSummary(channelId: string): Promise<{ allowed: number; blocked: Array<{ item_id: string; reasons: string[] }> }> {
    return request(`/channels/${channelId}/rights-summary`);
  },
  listJobs(filter: { status?: string; type?: string; limit?: number } = {}): Promise<Array<{
    id: string; type: string; status: string; progress: number; attempts: number; error: string | null;
    created_at: string; finished_at: string | null; result: Record<string, unknown> | null;
  }>> {
    const params = new URLSearchParams();
    if (filter.status) params.set("status", filter.status);
    if (filter.type) params.set("type", filter.type);
    if (filter.limit) params.set("limit", String(filter.limit));
    const qs = params.toString();
    return request(`/jobs${qs ? `?${qs}` : ""}`);
  },
  listPlaylists(channelSlug: string): Promise<Playlist[]> {
    return request(`/channels/${channelSlug}/playlists`);
  },
  getPlaylist(id: string): Promise<PlaylistWithItems> {
    return request(`/playlists/${id}`);
  },
  createPlaylist(channelSlug: string, input: Partial<Playlist> & { name: string; slug: string }): Promise<Playlist> {
    return request(`/channels/${channelSlug}/playlists`, { method: "POST", body: JSON.stringify(input) });
  },
  updatePlaylist(id: string, input: Partial<Playlist>): Promise<Playlist> {
    return request(`/playlists/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deletePlaylist(id: string): Promise<{ ok: true }> {
    return request(`/playlists/${id}`, { method: "DELETE" });
  },
  setPlaylistItems(id: string, items: Array<{ item_id: string; position: number; fade_in_ms?: number; fade_out_ms?: number }>): Promise<{ ok: true; count: number }> {
    return request(`/playlists/${id}/items`, { method: "PUT", body: JSON.stringify({ items }) });
  },
  listJingles(channelSlug: string): Promise<JingleWithLibrary[]> {
    return request(`/channels/${channelSlug}/jingles`);
  },
  createJingle(channelSlug: string, input: { name: string; slot_index: number; library_item_id: string; hotkey?: string; color?: string; gain_db?: number }): Promise<JingleWithLibrary> {
    return request(`/channels/${channelSlug}/jingles`, { method: "POST", body: JSON.stringify(input) });
  },
  updateJingle(id: string, input: { name?: string; hotkey?: string; color?: string; gain_db?: number }): Promise<JingleWithLibrary> {
    return request(`/jingles/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteJingle(id: string): Promise<{ ok: true }> {
    return request(`/jingles/${id}`, { method: "DELETE" });
  },
  getListenerStats(from: string, to: string, bucket: "minute" | "hour" | "day" = "hour"): Promise<{
    bucket: "minute" | "hour" | "day";
    from: string;
    to: string;
    points: Array<{ ts: string; listeners: number; sources: number }>;
    total: number;
    peak: number;
    peak_at: string | null;
    per_mount_peak: Array<{ mount: string; peak: number }>;
  }> {
    return request(`/tenants/me/listener-stats?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}`);
  },
  getNowPlaying(channelSlug: string): Promise<{
    mode: "live" | "autodj" | "off";
    live_connected: boolean;
    silent: boolean;
    engine: EngineState["state"];
    listeners: number;
    updated_at: string;
    track: { id: string; title: string; artist: string | null; duration_ms: number | null; started_at: string | null; kind: string } | null;
    next: PlayoutLogEntry[];
  }> {
    return request(`/channels/${channelSlug}/now-playing`);
  },
  getActivePlaylist(channelSlug: string): Promise<{
    playlist: { id: string; name: string; rotation_mode: string; current_position: number | null } | null;
    items: Array<{ id: string; position: number; item_id: string; title: string; artist: string | null; duration_ms: number | null }>;
  }> {
    return request(`/channels/${channelSlug}/playlist/active`);
  },
  getEncoders(channelSlug: string): Promise<{ engine: EngineState; streams: ChannelStream[] }> {
    return request(`/channels/${channelSlug}/encoders`);
  },
  listModerators(): Promise<Moderator[]> {
    return request(`/moderators`);
  },
  createModerator(input: { display_name: string; slug: string; bio_md?: string; photo_url?: string; contact_email?: string; is_public?: boolean; rolle?: string; gruppe?: string; position?: number }): Promise<Moderator> {
    return request(`/moderators`, { method: "POST", body: JSON.stringify(input) });
  },
  updateModerator(id: string, input: Partial<Moderator>): Promise<Moderator> {
    return request(`/moderators/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteModerator(id: string): Promise<{ ok: true }> {
    return request(`/moderators/${id}`, { method: "DELETE" });
  },
  listShows(channelId?: string): Promise<ShowWithRelations[]> {
    const q = channelId ? `?channel_id=${channelId}` : "";
    return request(`/shows${q}`);
  },
  createShow(input: { title: string; slug: string; channel_id?: string; description_md?: string; cover_url?: string; default_color?: string; moderator_id?: string; podcast_enabled?: boolean; intro_audio_id?: string }): Promise<Show> {
    return request(`/shows`, { method: "POST", body: JSON.stringify(input) });
  },
  updateShow(id: string, input: Partial<Show>): Promise<Show> {
    return request(`/shows/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteShow(id: string): Promise<{ ok: true }> {
    return request(`/shows/${id}`, { method: "DELETE" });
  },
  listSchedules(channelSlug: string): Promise<Schedule[]> {
    return request(`/channels/${channelSlug}/schedules`);
  },
  getSchedule(id: string): Promise<ScheduleWithBlocks> {
    return request(`/schedules/${id}`);
  },
  createSchedule(channelSlug: string, input: { name: string; is_default?: boolean; valid_from?: string; valid_until?: string }): Promise<Schedule> {
    return request(`/channels/${channelSlug}/schedules`, { method: "POST", body: JSON.stringify(input) });
  },
  deleteSchedule(id: string): Promise<{ ok: true }> {
    return request(`/schedules/${id}`, { method: "DELETE" });
  },
  renderSchedule(id: string): Promise<{ script: string; summary: Array<{ weekday: number; count: number; total_minutes: number }>; block_count: number }> {
    return request(`/schedules/${id}/render`);
  },
  addScheduleBlock(scheduleId: string, input: { weekday: number; start_time: string; end_time: string; kind: string; show_id?: string; playlist_id?: string; clock_id?: string; traffic_slot_id?: string; rotate_pool?: Array<{ weight: number; playlist_id?: string; show_id?: string }> }): Promise<{ id: string }> {
    return request(`/schedules/${scheduleId}/blocks`, { method: "POST", body: JSON.stringify(input) });
  },
  updateScheduleBlock(id: string, input: Record<string, unknown>): Promise<{ ok: true }> {
    return request(`/schedule-blocks/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteScheduleBlock(id: string): Promise<{ ok: true }> {
    return request(`/schedule-blocks/${id}`, { method: "DELETE" });
  },
  listTrafficSlots(channelSlug: string): Promise<Array<{ id: string; advertiser: string; spot_library_id: string | null; length_ms: number; weight: number; daypart_start: string | null; daypart_end: string | null; spot_title: string | null }>> {
    return request(`/channels/${channelSlug}/traffic-slots`);
  },
  createTrafficSlot(channelSlug: string, input: { advertiser: string; spot_library_id?: string; length_ms: number; weight?: number; daypart_start?: string; daypart_end?: string; notes?: string }): Promise<unknown> {
    return request(`/channels/${channelSlug}/traffic-slots`, { method: "POST", body: JSON.stringify(input) });
  },
  deleteTrafficSlot(id: string): Promise<{ ok: true }> {
    return request(`/traffic-slots/${id}`, { method: "DELETE" });
  },
  listEpisodes(channelId?: string, onlyPublished = true): Promise<Array<{ id: string; slug: string; title: string; published_at: string | null; duration_ms: number | null; audio_library_id: string | null; show_title: string | null; audio_title: string | null }>> {
    const params = new URLSearchParams();
    if (channelId) params.set("channel_id", channelId);
    if (!onlyPublished) params.set("published", "false");
    const qs = params.toString();
    return request(`/on-demand${qs ? `?${qs}` : ""}`);
  },
  createEpisode(input: { slug: string; title: string; channel_id?: string; show_id?: string; audio_library_id?: string; description_md?: string; cover_url?: string; duration_ms?: number; season?: number; episode_number?: number; explicit?: boolean }): Promise<{ id: string }> {
    return request(`/on-demand`, { method: "POST", body: JSON.stringify(input) });
  },
  publishEpisode(id: string): Promise<{ id: string; published_at: string }> {
    return request(`/on-demand/${id}/publish`, { method: "POST", body: JSON.stringify({}) });
  },
  unpublishEpisode(id: string): Promise<{ ok: true }> {
    return request(`/on-demand/${id}/unpublish`, { method: "POST", body: JSON.stringify({}) });
  },
  deleteEpisode(id: string): Promise<{ ok: true }> {
    return request(`/on-demand/${id}`, { method: "DELETE" });
  },
  getPodcastConfig(): Promise<{ title: string; description: string; author: string | null; email: string | null; language: string; image_url: string | null; category: string; copyright: string | null; feed_url: string }> {
    return request(`/podcast/config`);
  },
  savePodcastConfig(input: { title: string; description: string; author?: string; email?: string; language?: string; image_url?: string; category?: string; copyright?: string }): Promise<unknown> {
    return request(`/podcast/config`, { method: "PUT", body: JSON.stringify(input) });
  },
  getPodcastPreview(): Promise<string> {
    return fetch("/api/podcast/rss-preview", { credentials: "include" }).then((r) => r.text());
  },
  getScheduleRightsCheck(scheduleId: string): Promise<{
    schedule_id: string;
    schedule_name: string;
    blocks: Array<{ id: string; weekday: number; start_time: string; end_time: string; kind: string; title: string | null; allowed: boolean; blocking_items: Array<{ item_id: string; title: string; reasons: string[] }>; total_items: number }>;
    total_blocks: number;
    blocked_blocks: number;
  }> {
    return request(`/schedules/${scheduleId}/rights-check`);
  },

  // ---- Nachrichten ----
  listNews(area?: NewsArea, limit = 150): Promise<NewsItem[]> {
    const q = new URLSearchParams({ limit: String(limit) });
    if (area) q.set("area", area);
    return request(`/news?${q}`);
  },
  listWarnings(): Promise<NewsWarning[]> {
    return request("/news/warnings");
  },
  getNewsSettings(): Promise<NewsSettings> {
    return request("/news/settings");
  },
  searchNewsRegions(q: string, land?: string): Promise<Array<WarnCell & { land_name: string }>> {
    const p = new URLSearchParams({ q });
    if (land) p.set("land", land);
    return request(`/news/regions?${p}`);
  },
  saveNewsRegion(input: { land: string; warncells: string[]; add_suggestions?: boolean; places?: string[] }): Promise<NewsSettings> {
    return request("/news/settings/region", { method: "PUT", body: JSON.stringify(input) });
  },
  saveAutobahnen(input: { autobahnen: string; lat?: number | null; lon?: number | null; umkreis?: number; wetter?: boolean }): Promise<NewsSettings> {
    return request("/news/settings/verkehr", { method: "PUT", body: JSON.stringify(input) });
  },
  getVerkehr(): Promise<{ entries: Verkehrsmeldung[] }> {
    return request("/news/verkehr");
  },
  getWetter(): Promise<{ wetter: Wetterlage | null }> {
    return request("/news/wetter");
  },
  addNewsSource(input: { area: NewsArea; name: string; url: string; enabled?: boolean; preset_key?: string | null }): Promise<{ id: string }> {
    return request("/news/sources", { method: "POST", body: JSON.stringify(input) });
  },
  updateNewsSource(id: string, input: { enabled?: boolean; name?: string; area?: NewsArea }): Promise<{ ok: true }> {
    return request(`/news/sources/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteNewsSource(id: string): Promise<{ ok: true }> {
    return request(`/news/sources/${id}`, { method: "DELETE" });
  },
  testNewsSource(url: string): Promise<{ ok: true; title: string | null; count: number; items: Array<{ title: string; summary: string | null; link: string | null; published: string | null }> }> {
    return request("/news/sources/test", { method: "POST", body: JSON.stringify({ url }) });
  },
  refreshNews(): Promise<{ ok: true; added: number }> {
    return request("/news/refresh", { method: "POST", body: JSON.stringify({}) });
  },
  listNewsBlocks(): Promise<NewsBlock[]> {
    return request("/news/blocks");
  },
  // ---- Probebetrieb ----
  getPractice(): Promise<PracticeOverview> {
    return request("/practice");
  },
  createPracticeChannel(): Promise<{ id: string; slug: string; name: string }> {
    return request("/practice/channel", { method: "POST", body: JSON.stringify({}) });
  },
  givePracticeConsent(): Promise<{ consent_at: string }> {
    return request("/practice/consent", { method: "POST", body: JSON.stringify({}) });
  },
  startPractice(minutes?: number): Promise<{ session: PracticeSession; listen_url: string; ingest: LiveIngest | null }> {
    return request("/practice/start", { method: "POST", body: JSON.stringify(minutes ? { minutes } : {}) });
  },
  extendPractice(minutes = 30): Promise<PracticeSession> {
    return request("/practice/extend", { method: "POST", body: JSON.stringify({ minutes }) });
  },
  stopPractice(): Promise<PracticeSession> {
    return request("/practice/stop", { method: "POST", body: JSON.stringify({}) });
  },
  getPracticeSession(id: string): Promise<PracticeSessionDetail> {
    return request(`/practice/sessions/${id}`);
  },
  addPracticeNote(id: string, atMs: number, text: string): Promise<PracticeNote> {
    return request(`/practice/sessions/${id}/notes`, { method: "POST", body: JSON.stringify({ at_ms: Math.round(atMs), text }) });
  },
  approvePracticeSession(id: string): Promise<PracticeSession> {
    return request(`/practice/sessions/${id}/approve`, { method: "POST", body: JSON.stringify({}) });
  },
  deletePracticeRecording(id: string): Promise<{ ok: true; removed: boolean }> {
    return request(`/practice/sessions/${id}/recording`, { method: "DELETE" });
  },
  createTrainee(input: { email: string; display_name?: string; password: string }): Promise<{ id: string; name: string; email: string }> {
    return request("/practice/trainees", { method: "POST", body: JSON.stringify(input) });
  },
  promoteTrainee(userId: string): Promise<{ ok: true; name: string }> {
    return request(`/practice/trainees/${userId}/promote`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Mitschnitt als abspielbare Adresse (Blob, weil der Player keinen Anmelde-Header schicken kann). */
  async practiceRecordingUrl(id: string): Promise<string> {
    const res = await fetch(`/api/practice/sessions/${id}/recording`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    return URL.createObjectURL(await res.blob());
  },
  uploadNewsBlock(file: File, title: string, validUntil: string | null): Promise<{ id: string; valid_until: string; plays_at: string | null }> {
    const form = new FormData();
    form.append("file", file);
    if (title) form.append("title", title);
    if (validUntil) form.append("valid_until", validUntil);
    return fetch("/api/news/blocks", {
      method: "POST", body: form, credentials: "include", headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    }).then(async (res) => {
      if (!res.ok) throw fehlerAusAntwort(res.status, await res.text().catch(() => "")); // h-ui
      return (await res.json()) as { id: string; valid_until: string; plays_at: string | null };
    });
  },
  // ---- Grußwand, Titel bewerten, Hörer-Charts ----
  /** Nachricht auf die Grußwand stellen oder wieder herunternehmen. */
  setMessageWall(id: string, on: boolean): Promise<{ ok: true; wall: "on" | "off" }> {
    return request(`/messages/${id}/wall`, { method: "PUT", body: JSON.stringify({ on }) });
  },
  getRatings(slug: string, days = 30): Promise<{
    days: number;
    min_votes: number;
    affect_rotation: boolean;
    best: RatedTitle[];
    worst: RatedTitle[];
    thin: number;
  }> {
    return request(`/channels/${slug}/ratings?days=${days}`);
  },
  getCharts(slug: string): Promise<{
    size: number;
    period_days: number;
    playlist: string | null;
    week_start: string | null;
    computed_at: string | null;
    entries: ChartEntry[];
  }> {
    return request(`/channels/${slug}/charts`);
  },
  recomputeCharts(slug: string): Promise<{ ok: true; week_start: string | null; computed_at: string | null; entries: ChartEntry[] }> {
    return request(`/channels/${slug}/charts/recompute`, { method: "POST", body: JSON.stringify({}) });
  },
};

// ---- Probebetrieb ----

export interface PracticeHint {
  kind: "gut" | "hinweis" | "achtung";
  text: string;
  at_ms?: number;
}

export interface PracticeCheck {
  duration_ms: number;
  loudness_lufs: number | null;
  true_peak_db: number | null;
  silence: Array<{ at_ms: number; length_ms: number }>;
  hard_cuts_ms: number[];
  speech_share: number | null;
  longest_speech_ms: number | null;
  speech_segments: Array<{ from_ms: number; to_ms: number }>;
  talkover_db: number | null;
  tracks: number;
  jingles: number;
  hints: PracticeHint[];
}

export interface PracticeSession {
  id: string;
  user_name: string;
  started_at: string;
  ends_at: string;
  ended_at: string | null;
  end_reason: string | null;
  listen_url: string | null;
  recording: boolean;
  recording_ms: number | null;
  recording_expires_at: string | null;
  check: PracticeCheck | null;
  approved_at: string | null;
  approved_by_name: string | null;
  listeners: number;
}

export interface PracticeNote {
  id: string;
  author_name: string;
  at_ms: number;
  text: string;
  created_at: string;
}

export interface PracticeSessionDetail extends PracticeSession {
  notes: PracticeNote[];
  may_note: boolean;
  may_approve: boolean;
}

export interface PracticeOverview {
  channel: { id: string; slug: string; name: string } | null;
  may_practice: boolean;
  is_mentor: boolean;
  consent_at: string | null;
  recording_keep_days: number;
  default_minutes: number;
  running: PracticeSession | null;
  sessions: PracticeSession[];
  trainees: Array<{ id: string; name: string; email: string; consent: string | null }>;
}

export type NewsArea = "region" | "germany" | "world";

export interface NewsItem {
  id: string;
  area: NewsArea;
  title: string;
  summary: string | null;
  link: string | null;
  published_at: string | null;
  fetched_at: string;
  at: string;
  source: string | null;
}

export interface NewsWarning {
  id: string;
  warncell: string;
  region_name: string | null;
  level: number;
  level_label: string;
  color: string;
  event: string | null;
  headline: string | null;
  description: string | null;
  instruction: string | null;
  starts_at: string | null;
  ends_at: string | null;
}

export interface WarnCell {
  id: string;
  name: string;
  land: string;
}

export interface NewsSource {
  id: string;
  area: NewsArea;
  name: string;
  url: string;
  enabled: boolean;
  preset_key: string | null;
  last_fetch_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  fail_count: number;
  items: number;
}

/** Eine Verkehrsmeldung, wie die Nachrichtenseite sie zeigt. */
export interface Verkehrsmeldung {
  strasse: string;
  abschnitt: string;
  richtung: string | null;
  art: string;
  gesperrt: boolean;
  verzug: number | null;
  beginn: string | null;
  text: string | null;
  /** Alles in einem Satz – so, wie man es vorliest. */
  zeile: string;
}

/** Das Wetter am Standort des Radios. */
export interface Wetterlage {
  temperatur: number | null;
  icon: string;
  zustand: string;
  wind: number | null;
  tage: Array<{ datum: string; min: number | null; max: number | null; icon: string; zustand: string; regen: number | null }>;
  stand: string | null;
  zeile: string;
}

/** Die beiden Sendeplätze eines Kanals und wer gerade zu hören ist. */
export interface Uebergabe {
  platz_a: boolean;
  platz_b: boolean;
  sendeplatz: "a" | "b";
  gemeinsam: boolean;
  frei: "a" | "b" | null;
  angebot: { bis: string; von: string } | null;
  darf: boolean;
  ingest: { host: string; port: number; mount: string; user: string; password: string; platz: "a" | "b" } | null;
}

export interface NewsSettings {
  land: string | null;
  land_name: string | null;
  warncells: WarnCell[];
  sources: NewsSource[];
  suggestions: Array<{ key: string; area: NewsArea; name: string; url: string; added: boolean }>;
  laender: Record<string, string>;
  /** Beobachtete Autobahnen, z. B. ["A1","A48","A61"]. */
  autobahnen: string[];
  /** Mitte des Sendegebiets – ohne sie gilt kein Umkreis. */
  verkehr_ort: { lat: number; lon: number } | null;
  /** Umkreis in Kilometern; 0 heißt: alles, was auf der Autobahn los ist. */
  verkehr_umkreis: number;
  /** Wetter am Standort holen und zeigen. */
  wetter: boolean;
  legal_hint: string;
}

export interface NewsBlock {
  id: string;
  title: string;
  artist: string | null;
  duration_ms: number | null;
  ingested_at: string;
  valid_until: string | null;
  planned_at: string | null;
  last_played_at: string | null;
  expired: boolean;
  analysis_status: string;
}

// ---------------------------------------------------------------- Seiten-Baukasten

export interface SitePageSummary {
  id: string;
  slug: string;
  title: string;
  menu_label: string | null;
  in_menu: boolean;
  position: number;
  is_home: boolean;
  published_at: string | null;
  updated_at: string;
  has_changes: boolean;
  blocks_count: number;
}

export interface SitePageDetail extends SitePageSummary {
  seo_title: string | null;
  seo_description: string | null;
  blocks: PageBlock[];
  versions: Array<{ id: string; title: string; note: string | null; created_at: string; author: string | null }>;
}

export interface SiteOverview {
  pages: SitePageSummary[];
  settings: SiteSettings;
  templates: Array<{
    key: string; label: string; hint: string; pages: number;
    /** Aussehen, das zur Vorlage gehört – oder `null`, wenn sie keines mitbringt. */
    look: { font_set?: string; radius?: string; density?: string } | null;
  }>;
  channels: Array<{ slug: string; name: string }>;
  media: { used_bytes: number; quota_bytes: number; count: number };
  contact_open: number;
  public_url: string;
  tenant_slug: string;
  blocks: Array<{ kind: BlockKind; label: string; hint: string; group: string; fields: FieldDef[]; defaults: Record<string, unknown>; once: boolean }>;
}

export interface MediaItem {
  id: string;
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
  bytes: number;
  original_name: string | null;
  created_at: string;
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string | null;
  body: string;
  created_at: string;
  handled_at: string | null;
}

export interface SitePageInput {
  title: string;
  slug?: string;
  menu_label?: string | null;
  in_menu?: boolean;
  is_home?: boolean;
  seo_title?: string | null;
  seo_description?: string | null;
  blocks?: PageBlock[];
}

export const siteApi = {
  overview(): Promise<SiteOverview> {
    return request("/site");
  },
  page(id: string): Promise<SitePageDetail> {
    return request(`/site/pages/${id}`);
  },
  createPage(input: SitePageInput): Promise<SitePageSummary> {
    return request("/site/pages", { method: "POST", body: JSON.stringify(input) });
  },
  savePage(id: string, input: SitePageInput): Promise<SitePageSummary & { blocks: PageBlock[] }> {
    return request(`/site/pages/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  publishPage(id: string): Promise<SitePageSummary> {
    return request(`/site/pages/${id}/publish`, { method: "POST" });
  },
  unpublishPage(id: string): Promise<SitePageSummary> {
    return request(`/site/pages/${id}/unpublish`, { method: "POST" });
  },
  deletePage(id: string): Promise<{ ok: boolean }> {
    return request(`/site/pages/${id}`, { method: "DELETE" });
  },
  reorderPages(ids: string[]): Promise<{ ok: boolean }> {
    return request("/site/pages/order", { method: "POST", body: JSON.stringify({ ids }) });
  },
  previewLink(id: string): Promise<{ url: string; expires_in_minutes: number }> {
    return request(`/site/pages/${id}/preview`, { method: "POST" });
  },
  restoreVersion(pageId: string, versionId: string): Promise<SitePageSummary & { blocks: PageBlock[] }> {
    return request(`/site/pages/${pageId}/restore/${versionId}`, { method: "POST" });
  },
  saveSettings(settings: SiteSettings): Promise<{ settings: SiteSettings }> {
    return request("/site/settings", { method: "PUT", body: JSON.stringify(settings) });
  },
  applyTemplate(key: string, replace: boolean, look = false): Promise<{ ok: boolean; pages: string[]; aussehen: boolean; message: string }> {
    return request(`/site/templates/${key}`, { method: "POST", body: JSON.stringify({ replace, look }) });
  },
  media(): Promise<{ items: MediaItem[]; used_bytes: number; quota_bytes: number }> {
    return request("/site/media");
  },
  async uploadImage(file: File, alt: string, channelId: string | null = null): Promise<MediaItem> {
    const form = new FormData();
    form.append("alt", alt);
    form.append("file", file);
    // Aus einem Kanalbereich: für diesen Kanal (Recht über die Kanalrolle).
    const ziel = channelId ? `/api/site/media?channel_id=${encodeURIComponent(channelId)}` : "/api/site/media";
    const res = await fetch(ziel, {
      method: "POST",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
      body: form,
      credentials: "include",
    });
    const body = (await res.json().catch(() => ({}))) as { message?: string } & MediaItem;
    if (!res.ok && (body as { error?: string }).error === "limit_reached") throw fehlerAusAntwort(res.status, JSON.stringify(body)); // h-ui
    if (!res.ok) throw new Error(body.message ?? "Das Bild ließ sich nicht hochladen.");
    return body;
  },
  updateImage(id: string, alt: string): Promise<{ ok: boolean }> {
    return request(`/site/media/${id}`, { method: "PATCH", body: JSON.stringify({ alt }) });
  },
  deleteImage(id: string): Promise<{ ok: boolean }> {
    return request(`/site/media/${id}`, { method: "DELETE" });
  },
  contactMessages(): Promise<ContactMessage[]> {
    return request("/site/contact");
  },
  markContact(id: string, handled: boolean): Promise<{ ok: boolean }> {
    return request(`/site/contact/${id}`, { method: "PATCH", body: JSON.stringify({ handled }) });
  },
};

// ---------------------------------------------------------------- Redaktion

export interface PostCategory {
  id: string;
  slug: string;
  name: string;
  posts?: number;
}

export interface PostSummary {
  id: string;
  slug: string;
  title: string;
  teaser: string;
  cover_url: string | null;
  cover_alt: string;
  status: PostStatus;
  author_id: string | null;
  author: string | null;
  show_id: string | null;
  show_title: string | null;
  show_slug: string | null;
  reading_minutes: number;
  publish_at: string | null;
  published_at: string | null;
  updated_at: string;
  categories: PostCategory[];
}

export interface PostDetail extends PostSummary {
  blocks: PageBlock[];
  seo_title: string | null;
  seo_description: string | null;
  source_note: string | null;
  versions: Array<{ id: string; title: string; note: string | null; created_at: string; author: string | null }>;
}

export interface PostsOverview {
  tenant_slug: string;
  public_url: string;
  posts: PostSummary[];
  categories: PostCategory[];
  authors: Array<{ id: string; name: string; role: string; editorial_lead: boolean }>;
  shows: Array<{ id: string; title: string; slug: string }>;
  episodes: Array<{ slug: string; title: string }>;
  review_required: boolean;
  may_publish: boolean;
  may_edit: boolean;
  is_admin: boolean;
  waiting: number;
}

export interface PostInput {
  title: string;
  slug?: string;
  teaser?: string;
  cover_url?: string | null;
  cover_alt?: string;
  blocks?: PageBlock[];
  categories?: string[];
  show_id?: string | null;
  author_id?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
}

export interface PostFilter {
  status?: PostStatus;
  kategorie?: string;
  autor?: string;
  suche?: string;
}

export const postsApi = {
  overview(filter: PostFilter = {}): Promise<PostsOverview> {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) if (value) q.set(key, value);
    const s = q.toString();
    return request(`/redaktion${s ? `?${s}` : ""}`);
  },
  post(id: string): Promise<PostDetail> {
    return request(`/redaktion/posts/${id}`);
  },
  create(input: PostInput): Promise<PostSummary> {
    return request("/redaktion/posts", { method: "POST", body: JSON.stringify(input) });
  },
  save(id: string, input: PostInput): Promise<PostSummary & { blocks: PageBlock[] }> {
    return request(`/redaktion/posts/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  act(id: string, action: PostAction, extra: { publish_at?: string; note?: string } = {}): Promise<PostSummary> {
    return request(`/redaktion/posts/${id}/aktion`, { method: "POST", body: JSON.stringify({ action, ...extra }) });
  },
  remove(id: string): Promise<{ ok: boolean }> {
    return request(`/redaktion/posts/${id}`, { method: "DELETE" });
  },
  previewLink(id: string): Promise<{ url: string; expires_in_minutes: number }> {
    return request(`/redaktion/posts/${id}/vorschau`, { method: "POST" });
  },
  restoreVersion(postId: string, versionId: string): Promise<PostSummary & { blocks: PageBlock[] }> {
    return request(`/redaktion/posts/${postId}/fassung/${versionId}`, { method: "POST" });
  },
  createCategory(name: string): Promise<PostCategory> {
    return request("/redaktion/kategorien", { method: "POST", body: JSON.stringify({ name }) });
  },
  renameCategory(id: string, name: string): Promise<PostCategory> {
    return request(`/redaktion/kategorien/${id}`, { method: "PUT", body: JSON.stringify({ name }) });
  },
  deleteCategory(id: string): Promise<{ ok: boolean }> {
    return request(`/redaktion/kategorien/${id}`, { method: "DELETE" });
  },
  fromNews(newsId: string): Promise<{ id: string; slug: string; title: string; message: string }> {
    return request(`/redaktion/aus-meldung/${newsId}`, { method: "POST" });
  },
  setReviewRequired(value: boolean): Promise<{ review_required: boolean }> {
    return request("/redaktion/einstellungen", { method: "PUT", body: JSON.stringify({ review_required: value }) });
  },
  setEditorialLead(userId: string, value: boolean): Promise<{ ok: boolean }> {
    return request(`/redaktion/leitung/${userId}`, { method: "PUT", body: JSON.stringify({ editorial_lead: value }) });
  },
};

// ---- Mitmach-Module: Live-Abstimmung und Mitraten ----

export interface PollOption {
  id: string;
  label: string;
  votes: number;
  share: number;
  library_item_id: string | null;
  track: { title: string; artist: string | null } | null;
}

export interface Poll {
  id: string;
  question: string;
  status: "draft" | "open" | "closed";
  is_template: boolean;
  created_at: string;
  opened_at: string | null;
  closed_at: string | null;
  total: number;
  winner_option_id: string | null;
  options: PollOption[];
}

export interface QuizOption {
  id: string;
  label: string;
  votes: number;
  share: number;
  is_correct: boolean;
}

export interface QuizQuestion {
  id: string;
  question: string;
  status: "draft" | "open" | "revealed" | "closed";
  is_template: boolean;
  round_key: string;
  created_at: string;
  opened_at: string | null;
  revealed_at: string | null;
  closed_at: string | null;
  total: number;
  correct_option_id: string | null;
  correct_count: number;
  options: QuizOption[];
}

export interface QuizScore {
  nickname: string;
  points: number;
}

export const pollApi = {
  list(slug: string): Promise<Poll[]> {
    return request(`/channels/${slug}/polls`);
  },
  templates(slug: string): Promise<Poll[]> {
    return request(`/channels/${slug}/polls/templates`);
  },
  create(slug: string, input: { question: string; options: Array<{ label: string; library_item_id?: string | null }>; is_template?: boolean }): Promise<Poll> {
    return request(`/channels/${slug}/polls`, { method: "POST", body: JSON.stringify(input) });
  },
  get(id: string): Promise<Poll> {
    return request(`/polls/${id}`);
  },
  open(id: string): Promise<Poll> {
    return request(`/polls/${id}/open`, { method: "POST" });
  },
  close(id: string): Promise<Poll> {
    return request(`/polls/${id}/close`, { method: "POST" });
  },
  winnerToQueue(id: string): Promise<{ ok: true; plan_entry_id: string; title: string; message: string }> {
    return request(`/polls/${id}/winner-to-queue`, { method: "POST" });
  },
  remove(id: string): Promise<{ ok: true }> {
    return request(`/polls/${id}`, { method: "DELETE" });
  },
};

export const quizApi = {
  list(slug: string): Promise<QuizQuestion[]> {
    return request(`/channels/${slug}/quiz`);
  },
  templates(slug: string): Promise<QuizQuestion[]> {
    return request(`/channels/${slug}/quiz/templates`);
  },
  scoreboard(slug: string, round?: string): Promise<{ round_key: string; scores: QuizScore[] }> {
    return request(`/channels/${slug}/quiz/scoreboard${round ? `?round=${encodeURIComponent(round)}` : ""}`);
  },
  create(slug: string, input: { question: string; options: Array<{ label: string; is_correct: boolean }>; round_key?: string; is_template?: boolean }): Promise<QuizQuestion> {
    return request(`/channels/${slug}/quiz`, { method: "POST", body: JSON.stringify(input) });
  },
  get(id: string): Promise<QuizQuestion> {
    return request(`/quiz/${id}`);
  },
  open(id: string): Promise<QuizQuestion> {
    return request(`/quiz/${id}/open`, { method: "POST" });
  },
  reveal(id: string): Promise<QuizQuestion & { scoreboard: QuizScore[] }> {
    return request(`/quiz/${id}/reveal`, { method: "POST" });
  },
  close(id: string): Promise<QuizQuestion & { scoreboard: QuizScore[] }> {
    return request(`/quiz/${id}/close`, { method: "POST" });
  },
  remove(id: string): Promise<{ ok: true }> {
    return request(`/quiz/${id}`, { method: "DELETE" });
  },
};
export interface RatedTitle {
  item_id: string;
  title: string;
  artist: string | null;
  up: number;
  down: number;
  votes: number;
  share: number;
  solid: boolean;
}

export interface ChartEntry {
  position: number;
  title: string;
  artist: string | null;
  requests: number;
  up: number;
  down: number;
  score: number;
  move: number | null;
}

// ---- Mitmach-Modul: Gewinnspiel ----

export type ContestStatus = "draft" | "open" | "closed" | "drawn";
export type ContestMode = "mitmachen" | "frage" | "loesungswort";
export type ContactKind = "email" | "telefon";

export interface Contest {
  id: string;
  channel_id: string;
  title: string;
  description: string;
  prize: string;
  status: ContestStatus;
  /** Nimmt es gerade Teilnahmen an? Rechnet den Zeitraum schon mit ein. */
  running: boolean;
  mode: ContestMode;
  question: string;
  /** Die richtige Antwort – nur hier in der Suite, nie auf der Hörerseite. */
  answer: string;
  contact_kind: ContactKind;
  contact_required: boolean;
  terms: string;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  opened_at: string | null;
  closed_at: string | null;
  drawn_at: string | null;
  entries: number;
  valid_entries: number;
  winners: number;
}

/** Ein Gewinner. Nach der Löschfrist ohne Namen und ohne Kontakt. */
export interface ContestWinner {
  id: string;
  name: string;
  contact: string | null;
  won_at: string | null;
  purged: boolean;
}

/** Protokoll einer Ziehung: Zeitpunkt, Anzahl der Lose, wer gezogen hat. */
export interface ContestDrawLog {
  id: string;
  drawn_at: string;
  pool: number;
  entry_id: string | null;
  by: string | null;
}

export interface ContestEntry {
  id: string;
  name: string;
  contact: string | null;
  answer: string | null;
  valid: boolean;
  won: boolean;
  won_at: string | null;
  created_at: string;
  /** Name und Kontakt sind nach der Frist gelöscht; das Los bleibt. */
  purged: boolean;
}

export type ContestDetail = Contest & { winner_list: ContestWinner[]; draws: ContestDrawLog[] };

export interface ContestInput {
  title: string;
  description?: string;
  prize?: string;
  mode?: ContestMode;
  question?: string;
  answer?: string;
  contact_kind?: ContactKind;
  contact_required?: boolean;
  terms?: string;
  starts_at?: string | null;
  ends_at?: string | null;
}

export const contestApi = {
  list(slug: string): Promise<Contest[]> {
    return request(`/channels/${slug}/contests`);
  },
  termsTemplate(slug: string): Promise<{ terms: string; keep_days: number }> {
    return request(`/channels/${slug}/contests/terms-template`);
  },
  create(slug: string, input: ContestInput): Promise<Contest> {
    return request(`/channels/${slug}/contests`, { method: "POST", body: JSON.stringify(input) });
  },
  get(id: string): Promise<ContestDetail> {
    return request(`/contests/${id}`);
  },
  update(id: string, input: Partial<ContestInput>): Promise<Contest> {
    return request(`/contests/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  open(id: string): Promise<Contest> {
    return request(`/contests/${id}/open`, { method: "POST" });
  },
  close(id: string): Promise<Contest> {
    return request(`/contests/${id}/close`, { method: "POST" });
  },
  draw(id: string, count = 1): Promise<ContestDetail & { message: string; drawn: Array<{ id: string; name: string; pool: number }> }> {
    return request(`/contests/${id}/draw`, { method: "POST", body: JSON.stringify({ count }) });
  },
  entries(id: string): Promise<{ contest_id: string; keep_days: number; entries: ContestEntry[] }> {
    return request(`/contests/${id}/entries`);
  },
  markWinner(entryId: string, on: boolean): Promise<{ ok: true; won: boolean; winner_list: ContestWinner[] }> {
    return request(`/contest-entries/${entryId}/winner`, { method: "PUT", body: JSON.stringify({ on }) });
  },
  remove(id: string): Promise<{ ok: true }> {
    return request(`/contests/${id}`, { method: "DELETE" });
  },
  /** Lädt die Teilnahmen als CSV herunter und speichert sie im Browser. */
  async downloadEntries(id: string): Promise<void> {
    const res = await fetch(`/api/contests/${id}/entries.csv`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `gewinnspiel-${id}.csv`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ---- Mitmach-Modul: Frage der Woche ----

export interface VoiceSurvey {
  id: string;
  question: string;
  status: "draft" | "open" | "closed";
  run_days: number;
  created_at: string;
  opened_at: string | null;
  closes_at: string | null;
  closed_at: string | null;
  answers_total: number;
  answers_new: number;
  answers_approved: number;
  cut: { library_item_id: string; built_at: string; duration_ms: number } | null;
}

export interface VoiceSurveyAnswer {
  id: string;
  name: string | null;
  place: string | null;
  /** „Anna aus Prüm“ – schon fertig zusammengesetzt, nur mit dem Vornamen. */
  label: string;
  duration_ms: number | null;
  status: "new" | "approved" | "rejected";
  keyword: string | null;
  position: number | null;
  has_audio: boolean;
  created_at: string;
  decided_at: string | null;
  /** Zeitpunkt der Einwilligung; `consent_public_at` fehlt, wenn nur das Senden erlaubt ist. */
  consent_broadcast_at: string | null;
  consent_public_at: string | null;
  /** Der Wortlaut, dem der Hörer zugestimmt hat. */
  consent_text: string;
}

export type VoiceSurveyDetail = VoiceSurvey & { answers: VoiceSurveyAnswer[]; withdrawn: number };

export const voiceSurveyApi = {
  list(slug: string): Promise<VoiceSurvey[]> {
    return request(`/channels/${slug}/voice-surveys`);
  },
  create(slug: string, input: { question: string; run_days?: number }): Promise<VoiceSurvey> {
    return request(`/channels/${slug}/voice-surveys`, { method: "POST", body: JSON.stringify(input) });
  },
  get(id: string): Promise<VoiceSurveyDetail> {
    return request(`/voice-surveys/${id}`);
  },
  open(id: string): Promise<VoiceSurvey> {
    return request(`/voice-surveys/${id}/open`, { method: "POST" });
  },
  close(id: string): Promise<VoiceSurvey> {
    return request(`/voice-surveys/${id}/close`, { method: "POST" });
  },
  remove(id: string): Promise<{ ok: true }> {
    return request(`/voice-surveys/${id}`, { method: "DELETE" });
  },
  /** Reihenfolge des O-Ton-Blocks: die Kennungen in der gewünschten Folge. */
  setOrder(id: string, ids: string[]): Promise<{ ok: true; ordered: number }> {
    return request(`/voice-surveys/${id}/order`, { method: "PUT", body: JSON.stringify({ ids }) });
  },
  /** Baut aus den freigegebenen Antworten eine Datei in der Mediathek. */
  buildCut(id: string, pauseMs?: number): Promise<{ ok: true; library_item_id: string; title: string; answers: number; duration_ms: number; message: string }> {
    return request(`/voice-surveys/${id}/cut`, { method: "POST", body: JSON.stringify(pauseMs === undefined ? {} : { pause_ms: pauseMs }) });
  },
  patchAnswer(id: string, patch: { status?: "new" | "approved" | "rejected"; keyword?: string | null; position?: number | null }): Promise<VoiceSurveyAnswer> {
    return request(`/voice-survey-answers/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  blockAnswer(id: string): Promise<{ ok: true; message: string }> {
    return request(`/voice-survey-answers/${id}/block`, { method: "POST" });
  },
  /** Aufnahme als Objekt-Adresse, damit sie sich im Browser anhören lässt. */
  async answerAudioUrl(id: string): Promise<string> {
    const res = await fetch(`/api/voice-survey-answers/${id}/audio`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    return URL.createObjectURL(await res.blob());
  },
};

// ---- Hörertelefon: Warteschlange, Anrufe, Sperrliste ----

/** Ein Anrufer, wie ihn Studio und Suite in der Warteschlange sehen. */
export interface PhoneQueueEntry {
  id: string;
  name: string;
  place: string | null;
  topic: string | null;
  status: "waiting" | "preview" | "air" | "done";
  position: number;
  waiting_seconds: number;
  waiting_text: string;
  connected: boolean;
}

/** Stand der Leitung: offen oder zu, wer sie geöffnet hat, wer wartet. */
export interface PhoneState {
  open: boolean;
  open_until: string | null;
  opened_by: string | null;
  studio_connected: boolean;
  queue_max: number;
  ask_place: boolean;
  note: string;
  queue: PhoneQueueEntry[];
}

/** Ein Anruf aus dem Rückblick – auch die schon beendeten. */
export interface PhoneCall {
  id: string;
  listener_name: string;
  place: string | null;
  topic: string | null;
  status: "waiting" | "preview" | "air" | "done";
  created_at: string;
  first_air_at: string | null;
  ended_at: string | null;
  air_seconds: number;
  end_reason: "listener" | "studio" | "blocked" | "timeout" | null;
}

/** Ein gesperrtes Gerät. Der Prüfwert wechselt um Mitternacht von selbst. */
export interface PhoneBlocked {
  client_hash: string;
  until: string;
  last_name: string | null;
}

// ---- Hörerchat ----

/** Eine Zeile im Chatraum, so wie API und Verbindung sie liefern. */
export interface ChatEntry {
  id: string;
  at: string;
  nick: string;
  rang: "gast" | "team" | "studio";
  kind: "text" | "aktion" | "system";
  text: string;
}

/** Wer gerade im Raum ist. */
export interface ChatPerson {
  id: string;
  nick: string;
  rang: "gast" | "team" | "studio";
  seit: number;
}

export interface ChatState {
  settings: { guests: boolean; only_on_air: boolean; slow_seconds: number; keep_hours: number; welcome: string; rules: string };
  open: boolean;
  online: ChatPerson[];
  entries: ChatEntry[];
}

export const chatApi = {
  state(slug: string): Promise<ChatState> {
    return request(`/channels/${slug}/chat`);
  },
  /** Schlüssel fürs Team – damit verbindet sich die Suite (und das Studio). */
  token(slug: string): Promise<{ token: string; nick: string; ws_url: string }> {
    return request(`/channels/${slug}/chat/token`, { method: "POST", body: JSON.stringify({}) });
  },
  hide(id: string): Promise<{ ok: true }> {
    return request(`/chat/messages/${id}`, { method: "DELETE" });
  },
};

export const phoneApi = {
  state(slug: string): Promise<PhoneState> {
    return request(`/channels/${slug}/phone`);
  },
  setOpen(slug: string, open: boolean, minutes?: number): Promise<{ open: boolean; open_until: string | null; studio_token: string | null; ws_url?: string }> {
    return request(`/channels/${slug}/phone/open`, { method: "PUT", body: JSON.stringify({ open, minutes }) });
  },
  calls(slug: string): Promise<PhoneCall[]> {
    return request(`/channels/${slug}/phone/calls`);
  },
  blocked(slug: string): Promise<PhoneBlocked[]> {
    return request(`/channels/${slug}/phone/blocked`);
  },
  unblock(slug: string, hash: string): Promise<{ ok: true }> {
    return request(`/channels/${slug}/phone/blocked/${hash}`, { method: "DELETE" });
  },
  preview(id: string): Promise<{ ok: true; status: string; queue: PhoneQueueEntry[] }> {
    return request(`/phone/calls/${id}/preview`, { method: "POST" });
  },
  air(id: string): Promise<{ ok: true; status: string; queue: PhoneQueueEntry[] }> {
    return request(`/phone/calls/${id}/air`, { method: "POST" });
  },
  hangup(id: string): Promise<{ ok: true; status: string; queue: PhoneQueueEntry[] }> {
    return request(`/phone/calls/${id}/hangup`, { method: "POST" });
  },
  block(id: string): Promise<{ ok: true; message: string; queue: PhoneQueueEntry[] }> {
    return request(`/phone/calls/${id}/block`, { method: "POST" });
  },
};

/** Modul „Erinnerungen“: Zahlen, keine Personen. */
export interface ReminderReport {
  shows: Array<{ id: string; title: string; reminders: number; next: string | null }>;
  requests: number;
  delivered: number;
  failed: number;
  cleaned: number;
  days: number;
}

export const remindersApi = {
  report(slug: string, days = 30): Promise<ReminderReport> {
    return request(`/channels/${slug}/reminders?days=${days}`);
  },
};

// ---------------------------------------------------------------- Mitmach-Kalender

export interface EventItem {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string | null;
  place: string;
  address: string;
  organizer: string;
  description: string;
  admission: string;
  link: string;
  category: string | null;
  category_id: string | null;
  status: EventStatus;
  reject_reason: string | null;
  /** Nur für Rückfragen, nie öffentlich – und nach dem Termin leer. */
  contact_name: string | null;
  contact_mail: string | null;
  contact_phone: string | null;
  contact_cleared: boolean;
  source: "hoerer" | "redaktion";
  series_id: string | null;
  merged_into: string | null;
  was_public: boolean;
  sequence: number;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventCategory {
  id: string;
  slug: string;
  name: string;
  events?: number;
}

export interface EventRule {
  freq: "woechentlich" | "monatlich";
  interval: number;
  weekday: number;
  nth: number;
  start_date: string;
  time: string;
  duration_minutes: number;
  until: string;
}

export interface EventSeries {
  id: string;
  label: string;
  title: string;
  events: number;
  created_at: string;
  rule: EventRule;
}

/** Was die Kalenderseite der Suite auf einmal bekommt. */
export interface EventsOverview {
  events: EventItem[];
  categories: EventCategory[];
  series: EventSeries[];
  waiting: number;
  may_publish: boolean;
  may_edit: boolean;
  is_admin: boolean;
  tenant_slug: string;
  region: string;
  days_ahead: number;
  time_zone: string;
}

/** Felder eines Termins, wie sie im Formular der Suite stehen. */
export interface EventInput {
  title: string;
  date: string;
  time: string;
  end_time?: string;
  end_date?: string;
  place?: string;
  address?: string;
  organizer?: string;
  description?: string;
  admission?: string;
  link?: string;
  category_id?: string | null;
  contact_name?: string;
  contact_mail?: string;
  contact_phone?: string;
}

export const eventsApi = {
  overview(filter: { status?: string; suche?: string; von?: string } = {}): Promise<EventsOverview> {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) if (value) q.set(key, value);
    const s = q.toString();
    return request(`/kalender${s ? `?${s}` : ""}`);
  },
  create(input: EventInput): Promise<EventItem> {
    return request("/kalender/termine", { method: "POST", body: JSON.stringify(input) });
  },
  save(id: string, input: EventInput): Promise<EventItem> {
    return request(`/kalender/termine/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  act(id: string, action: EventAction, extra: { reason?: string } = {}): Promise<EventItem> {
    return request(`/kalender/termine/${id}/aktion`, { method: "POST", body: JSON.stringify({ action, ...extra }) });
  },
  merge(id: string, into: string): Promise<{ ok: true; event: EventItem; into: EventItem }> {
    return request(`/kalender/termine/${id}/zusammenfuehren`, { method: "POST", body: JSON.stringify({ into }) });
  },
  duplicates(id: string): Promise<Array<EventItem & { similarity: number }>> {
    return request(`/kalender/termine/${id}/doppelte`);
  },
  remove(id: string): Promise<{ ok: true; cancelled: boolean; message?: string }> {
    return request(`/kalender/termine/${id}`, { method: "DELETE" });
  },
  addCategory(name: string): Promise<EventCategory> {
    return request("/kalender/kategorien", { method: "POST", body: JSON.stringify({ name }) });
  },
  saveCategory(id: string, input: { name?: string; position?: number }): Promise<{ ok: true }> {
    return request(`/kalender/kategorien/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  removeCategory(id: string): Promise<{ ok: true }> {
    return request(`/kalender/kategorien/${id}`, { method: "DELETE" });
  },
  createSeries(input: Omit<EventInput, "date" | "time" | "end_date"> & { rule: EventRule }): Promise<{ id: string; label: string; count: number; message: string }> {
    return request("/kalender/serien", { method: "POST", body: JSON.stringify(input) });
  },
  removeSeries(id: string): Promise<{ ok: true }> {
    return request(`/kalender/serien/${id}`, { method: "DELETE" });
  },
  /** Kalenderblock für die Sendung: fertiger Text zum Ausdrucken oder Ablesen. */
  announce(von?: string, bis?: string): Promise<{ from: string; to: string; count: number; text: string }> {
    const q = new URLSearchParams();
    if (von) q.set("von", von);
    if (bis) q.set("bis", bis);
    const s = q.toString();
    return request(`/kalender/ansage${s ? `?${s}` : ""}`);
  },
};

// ---------------------------------------------------------------- Mitschnitt

/** Ein Kanal im Überblick des Mitschnitts. */
export interface MitschnittChannel {
  id: string;
  slug: string;
  name: string;
  status: string;
  enabled: boolean;
  keep_days: number;
  bitrate_kbps: number;
  running: boolean;
  last_error: string | null;
  bytes: number;
  hours: number;
  oldest: string | null;
  /** Platzbedarf, wenn die Aufbewahrungsfrist voll ausgeschöpft ist. */
  expected_bytes: number;
}

export interface MitschnittOverview {
  may_edit: boolean;
  time_zone: string;
  keep_days_min: number;
  keep_days_max: number;
  bitrates: number[];
  channels: MitschnittChannel[];
}

/** Ein Tag mit Aufnahmen – in der Zeitzone des Senders. */
export interface MitschnittDay {
  day: string;
  hours: number;
  bytes: number;
}

/** Eine aufgezeichnete Stunde. */
export interface MitschnittHour {
  id: string;
  hour_start: string;
  /** „16.09.2026, 18 Uhr“ – so, wie die Uhr beim Sender stand. */
  label: string;
  bytes: number;
  seconds: number;
  bitrate_kbps: number;
  source: "server" | "studio";
  /** Die Stunde läuft noch; die Datei wächst. */
  running: boolean;
}

/** Endpunkte des Mitschnitts – eigenes Objekt, wie beim Kalender darüber. */
export const mitschnittApi = {
  overview(): Promise<MitschnittOverview> {
    return request("/mitschnitt");
  },
  save(slug: string, input: { enabled?: boolean; keep_days?: number; bitrate_kbps?: number }): Promise<{ expected_bytes: number }> {
    return request(`/channels/${slug}/mitschnitt`, { method: "PUT", body: JSON.stringify(input) });
  },
  days(slug: string): Promise<{ time_zone: string; days: MitschnittDay[] }> {
    return request(`/channels/${slug}/mitschnitt/tage`);
  },
  hours(slug: string, day: string): Promise<{ day: string; time_zone: string; hours: MitschnittHour[] }> {
    return request(`/channels/${slug}/mitschnitt/stunden?tag=${encodeURIComponent(day)}`);
  },
  /** Zum Abspielen: Der <audio>-Player kann keinen Anmelde-Header schicken. */
  async blobUrl(id: string): Promise<string> {
    const res = await fetch(`/api/mitschnitt/${id}/anhoeren`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    return URL.createObjectURL(await res.blob());
  },
  /** Herunterladen mit Anmeldung und im Browser speichern. */
  async download(id: string, name: string): Promise<void> {
    const res = await fetch(`/api/mitschnitt/${id}/datei`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

/**
 * Taktanalyse: Korrektur von Hand und das Nachrechnen des Bestands.
 *
 * Bewusst am Ende der Datei und als eigenes Objekt – so muss für einen neuen
 * Endpunkt niemand mitten in `api` hineinschreiben.
 */
export const taktApi = {
  /**
   * Tempo oder Tonart von Hand setzen. Was hier ankommt, gilt als „von Hand“ und
   * wird von keiner Messung mehr überschrieben. `null` gibt den Wert wieder frei.
   */
  korrigieren(
    id: string,
    input: { bpm?: number | null; beats_per_bar?: number | null; first_beat_ms?: number | null; musical_key?: string | null; musical_key_camelot?: string | null }
  ): Promise<Partial<LibraryItem>> {
    return request(`/library/${id}/takt`, { method: "PATCH", body: JSON.stringify(input) });
  },
  /** Einen einzelnen Titel neu vermessen. */
  messen(id: string): Promise<{ ok: true }> {
    return request(`/library/${id}/takt/messen`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Stand der Nachrechnung: letzter Lauf und wie viele Titel noch offen sind. */
  nachrechnenStand(): Promise<{ run: TaktBackfillRun | null; offen: number }> {
    return request("/library/takt/nachrechnen");
  },
  nachrechnenStarten(): Promise<{ run: TaktBackfillRun }> {
    return request("/library/takt/nachrechnen", { method: "POST", body: JSON.stringify({}) });
  },
  nachrechnenAbbrechen(): Promise<{ gestoppt: boolean }> {
    return request("/library/takt/nachrechnen", { method: "DELETE" });
  },
};

// ---------------------------------------------------------------- Private Veranstaltungen

/** Eine gebuchte Feier, so wie die Suite sie sieht. */
export interface FestEintrag {
  id: string;
  slug: string;
  title: string;
  kind: string;
  status: "angefragt" | "bestaetigt" | "abgesagt";
  starts_at: string;
  ends_at: string;
  day_label: string;
  time_label: string;
  end_label: string;
  channel: string | null;
  lead_minutes: number;
  trail_minutes: number;
  guests_expected: number;
  wishes: string;
  contact_name: string;
  contact_mail: string;
  contact_phone: string;
  /** Was vereinbart wurde. Bezahlt wird außerhalb der Suite. */
  note: string;
  reply: string;
  reject_reason: string | null;
  device_limit: number;
  /** Von wann bis wann eine Einladung gilt – mit Vor- und Nachlauf. */
  access_from: string;
  access_to: string;
  gift_until: string | null;
  has_gift: boolean;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FestUebersicht {
  events: FestEintrag[];
  waiting: number;
  may_edit: boolean;
  is_admin: boolean;
  tenant_slug: string;
  time_zone: string;
  channel: { id: string; slug: string; name: string; status: string } | null;
  settings: { lead_minutes: number; trail_minutes: number; device_limit: number; gift_days: number };
}

/** Eine Einladung – mit dem Schlüssel, denn der Gastgeber druckt ihn aus. */
export interface FestEinladung {
  id: string;
  guest_name: string;
  guest_contact: string;
  key: string;
  key_hint: string;
  device_limit: number | null;
  revoked: boolean;
  revoked_at: string | null;
  sent_at: string | null;
  first_used_at: string | null;
  last_used_at: string | null;
  /** Wie viele Geräte gerade damit hören. */
  devices_now: number;
  created_at: string;
  link: string;
}

export interface FestBeitrag {
  id: string;
  kind: "gruss" | "wunsch";
  name: string;
  text: string;
  done: boolean;
  at: string;
  time_label: string;
}

export interface FestEingabe {
  title: string;
  kind?: string;
  date: string;
  time: string;
  end_time: string;
  end_date?: string;
  guests?: number;
  wishes?: string;
  contact_name?: string;
  contact_mail?: string;
  contact_phone?: string;
  note?: string;
  lead_minutes?: number;
  trail_minutes?: number;
  device_limit?: number;
}

/**
 * Private Veranstaltungen. Bewusst am Ende der Datei und als eigenes Objekt – so
 * muss für einen neuen Endpunkt niemand mitten in `api` hineinschreiben.
 */
export const veranstaltungApi = {
  uebersicht(status = "alle"): Promise<FestUebersicht> {
    return request(`/veranstaltungen?status=${encodeURIComponent(status)}`);
  },
  anlegen(input: FestEingabe): Promise<FestEintrag> {
    return request("/veranstaltungen", { method: "POST", body: JSON.stringify(input) });
  },
  speichern(id: string, input: FestEingabe): Promise<FestEintrag> {
    return request(`/veranstaltungen/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  aktion(id: string, input: { action: "bestaetigen" | "absagen"; reason?: string; reply?: string; note?: string }): Promise<FestEintrag> {
    return request(`/veranstaltungen/${id}/aktion`, { method: "POST", body: JSON.stringify(input) });
  },
  loeschen(id: string): Promise<{ ok: true }> {
    return request(`/veranstaltungen/${id}`, { method: "DELETE" });
  },
  einladungen(id: string): Promise<{ page: string; invites: FestEinladung[] }> {
    return request(`/veranstaltungen/${id}/einladungen`);
  },
  einladen(
    id: string,
    input: { guests?: Array<{ name: string; contact: string }>; count?: number; device_limit?: number }
  ): Promise<{ created: number; invites: FestEinladung[] }> {
    return request(`/veranstaltungen/${id}/einladungen`, { method: "POST", body: JSON.stringify(input) });
  },
  verschickt(id: string, eid: string): Promise<{ ok: true }> {
    return request(`/veranstaltungen/${id}/einladungen/${eid}/verschickt`, { method: "POST", body: JSON.stringify({}) });
  },
  zurueckziehen(id: string, eid: string): Promise<{ ok: true; message: string }> {
    return request(`/veranstaltungen/${id}/einladungen/${eid}`, { method: "DELETE" });
  },
  /** Der QR-Code zum Ausdrucken. Ein <img> kann keinen Anmelde-Header schicken, deshalb hier. */
  async qrSvg(id: string, eid: string): Promise<string> {
    const res = await fetch(`/api/veranstaltungen/${id}/einladungen/${eid}/qr.svg`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    return res.text();
  },
  beitraege(id: string): Promise<FestBeitrag[]> {
    return request(`/veranstaltungen/${id}/beitraege`);
  },
  mitschnitt(id: string): Promise<{ hours: Array<{ id: string; label: string; bytes: number }>; gift_until: string | null; has_gift: boolean }> {
    return request(`/veranstaltungen/${id}/mitschnitt`);
  },
  geschenk(id: string, days?: number): Promise<{ ok: true; url: string; days: number }> {
    return request(`/veranstaltungen/${id}/geschenk`, { method: "POST", body: JSON.stringify(days ? { days } : {}) });
  },
  geschenkWeg(id: string): Promise<{ ok: true }> {
    return request(`/veranstaltungen/${id}/geschenk`, { method: "DELETE" });
  },
  kanalAnlegen(name: string): Promise<{ channel: { id: string; slug: string; name: string }; message: string }> {
    return request("/veranstaltungen/kanal", { method: "POST", body: JSON.stringify({ name }) });
  },
  /** Die Playliste des Abends – die braucht ihr für die GEMA-Meldung. */
  async playliste(id: string, name: string): Promise<void> {
    const res = await fetch(`/api/veranstaltungen/${id}/playliste.csv`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = `playliste-${name}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ---------------------------------------------------------------------------
// Posteingang
// ---------------------------------------------------------------------------

export type PosteingangArt = "kontakt" | "studio" | "wunsch" | "oton" | "veranstaltung" | "stoerung";
export type PosteingangStatus = "neu" | "in_arbeit" | "erledigt";

export interface PosteingangEintrag {
  id: string;
  art: PosteingangArt;
  quelle_tabelle: string;
  quelle_id: string;
  absender: string | null;
  vorschau: string | null;
  eingegangen_at: string;
  status: PosteingangStatus;
  gelesen_at: string | null;
  erledigt_at: string | null;
  channel_id: string | null;
  channel_name: string | null;
  channel_slug: string | null;
  zugewiesen_an: string | null;
  zugewiesen_name: string | null;
  client_hash: string | null;
  meta: Record<string, unknown>;
  antwort_moeglich?: boolean;
}

export interface PosteingangAntwort {
  id: string;
  text: string;
  an: string;
  gesendet_at: string | null;
  fehler: string | null;
  angelegt_at: string;
  verfasst_von: string | null;
}

export interface PosteingangDetail {
  eintrag: PosteingangEintrag;
  art_text: string;
  quelle: Record<string, unknown> | null;
  quelle_fehlt: boolean;
  antwort_an: string | null;
  antworten: PosteingangAntwort[];
}

export interface StummerAbsender {
  client_hash: string;
  grund: string | null;
  bis: string | null;
  angelegt_at: string;
  angelegt_von: string | null;
}

export const posteingangApi = {
  liste(filter: { status?: string; art?: string; kanal?: string; mir?: boolean; vor?: string; limit?: number } = {}): Promise<{
    arten: Record<PosteingangArt, string>;
    eintraege: PosteingangEintrag[];
    weiter: string | null;
  }> {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.art) q.set("art", filter.art);
    if (filter.kanal) q.set("kanal", filter.kanal);
    if (filter.mir) q.set("mir", "1");
    if (filter.vor) q.set("vor", filter.vor);
    if (filter.limit) q.set("limit", String(filter.limit));
    const s = q.toString();
    return request(`/posteingang${s ? `?${s}` : ""}`);
  },
  zaehler(): Promise<{ neu: number; offen: number; nach_art: Partial<Record<PosteingangArt, number>> }> {
    return request("/posteingang/zaehler");
  },
  detail(id: string): Promise<PosteingangDetail> {
    return request(`/posteingang/${id}`);
  },
  aendern(id: string, input: { status?: PosteingangStatus; zugewiesen_an?: string | null }): Promise<{ eintrag: PosteingangEintrag }> {
    return request(`/posteingang/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  antworten(id: string, text: string): Promise<{ ok: true; an: string }> {
    return request(`/posteingang/${id}/antwort`, { method: "POST", body: JSON.stringify({ text }) });
  },
  stumm(id: string, input: { grund?: string; tage?: number | null } = {}): Promise<{ ok: true; entfernt: number; bis: number | null }> {
    return request(`/posteingang/${id}/stumm`, { method: "POST", body: JSON.stringify(input) });
  },
  stummListe(): Promise<{ stumm: StummerAbsender[] }> {
    return request("/posteingang/stumm");
  },
  stummAufheben(hash: string): Promise<{ ok: true }> {
    return request(`/posteingang/stumm/${encodeURIComponent(hash)}`, { method: "DELETE" });
  },
};

/** Eine Kachel auf dem Dashboard: eine Zahl mit einem Ziel. */
export interface DashboardKachel {
  id: string;
  label: string;
  wert: number;
  einheit?: string;
  hinweis?: string;
  ziel: string;
  ton: "gut" | "ruhig" | "achtung" | "warnung";
}

// ---- Werbeplanung (f-werbung) ----
// Eigener Block statt der alten listTrafficSlots/createTrafficSlot oben: die
// kannten Zeitraum und Notiz nicht und hatten kein Ändern.
type WerbeEintrag = import("./types").TrafficSlotEintrag;
type WerbeEingabe = import("./types").TrafficSlotEingabe;

export const werbungApi = {
  liste(channelSlug: string): Promise<WerbeEintrag[]> {
    return request(`/channels/${encodeURIComponent(channelSlug)}/traffic-slots`);
  },
  anlegen(channelSlug: string, input: WerbeEingabe): Promise<WerbeEintrag> {
    return request(`/channels/${encodeURIComponent(channelSlug)}/traffic-slots`, { method: "POST", body: JSON.stringify(input) });
  },
  /** Nur die übergebenen Felder ändern sich; null leert Tageszeit, Zeitraum oder Notiz. */
  aendern(id: string, input: Partial<WerbeEingabe>): Promise<WerbeEintrag> {
    return request(`/traffic-slots/${id}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  loeschen(id: string): Promise<{ ok: true }> {
    return request(`/traffic-slots/${id}`, { method: "DELETE" });
  },
};

// ---- f-regeln: Musikregeln ----

/** Bedingungen einer Musikregel. Alle UND-verknüpft, jede optional (siehe apps/api/src/playout/music-rules.ts). */
export interface MusikRegelBedingungen {
  bpm_min?: number | null;
  bpm_max?: number | null;
  bpm_sicherheit_min?: number | null;
  jahr_von?: number | null;
  jahr_bis?: number | null;
  tags_irgendeiner?: string[];
  tags_alle?: string[];
  tags_keiner?: string[];
  dauer_max_s?: number | null;
  interpret_enthaelt?: string | null;
  interpret_nicht?: string | null;
  tonarten?: string[];
}

export interface MusikRegel {
  id: string;
  name: string;
  farbe: string;
  bedingungen: MusikRegelBedingungen;
  anzahl: number;
  in_uhren: number;
  updated_at: string;
}

export interface MusikRegelVorschau {
  anzahl: number;
  dauer_ms: number;
  gesamt: number;
  ohne_wert: { tempo?: number; jahr?: number; tonart?: number; dauer?: number };
  beispiele: Array<{
    id: string; title: string; artist: string | null; duration_ms: number | null;
    bpm: number | null; bpm_source: string; bpm_confidence: number | null; jahr: number | null; tonart: string | null;
  }>;
}

export const musikregelApi = {
  liste(): Promise<{ regeln: MusikRegel[] }> {
    return request("/music-rules");
  },
  anlegen(input: { name: string; farbe: string; bedingungen: MusikRegelBedingungen }): Promise<{ id: string }> {
    return request("/music-rules", { method: "POST", body: JSON.stringify(input) });
  },
  aendern(id: string, input: { name: string; farbe: string; bedingungen: MusikRegelBedingungen }): Promise<{ ok: true }> {
    return request(`/music-rules/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  loeschen(id: string): Promise<{ ok: true }> {
    return request(`/music-rules/${id}`, { method: "DELETE" });
  },
  vorschau(bedingungen: MusikRegelBedingungen): Promise<MusikRegelVorschau> {
    return request("/music-rules/vorschau", { method: "POST", body: JSON.stringify({ bedingungen }) });
  },
};

// ---- f-auth: Passwort vergessen und Zwei-Faktor-Anmeldung ----------------------

export type LoginAntwort =
  | { access_token: string; user: SessionUser }
  | { two_factor_required: true; challenge: string };

export interface ZweiFaktorStatus {
  enabled: boolean;
  enabled_at: string | null;
  recovery_codes_left: number;
}

export const kontoApi = {
  /** Was diese Installation kann, bevor jemand angemeldet ist. */
  features(): Promise<{ mail: boolean }> {
    return request("/auth/features");
  },
  forgot(email: string): Promise<{ ok: true; message: string }> {
    return request("/auth/forgot", { method: "POST", body: JSON.stringify({ email }) });
  },
  reset(token: string, password: string): Promise<{ ok: true }> {
    return request("/auth/reset", { method: "POST", body: JSON.stringify({ token, password }) });
  },
  loginZweiFaktor(challenge: string, code: string): Promise<{ access_token: string; user: SessionUser }> {
    return request("/auth/login/2fa", { method: "POST", body: JSON.stringify({ challenge, code }) }).then((r) => {
      const res = r as { access_token: string; user: SessionUser };
      setAccessToken(res.access_token);
      return res;
    });
  },
  status(): Promise<ZweiFaktorStatus> {
    return request("/auth/2fa");
  },
  einrichten(password: string): Promise<{ secret: string; otpauth_uri: string; qr_svg: string }> {
    return request("/auth/2fa/setup", { method: "POST", body: JSON.stringify({ password }) });
  },
  einschalten(code: string): Promise<{ ok: true; recovery_codes: string[] }> {
    return request("/auth/2fa/enable", { method: "POST", body: JSON.stringify({ code }) });
  },
  abschalten(password: string, code: string): Promise<{ ok: true }> {
    return request("/auth/2fa/disable", { method: "POST", body: JSON.stringify({ password, code }) });
  },
  neueCodes(password: string, code: string): Promise<{ ok: true; recovery_codes: string[] }> {
    return request("/auth/2fa/recovery-codes", { method: "POST", body: JSON.stringify({ password, code }) });
  },
  /** Radioleitung: Zwei-Faktor einer Person abschalten (Handy verloren). */
  teamZweiFaktorZuruecksetzen(userId: string): Promise<{ ok: true }> {
    return request(`/users/${userId}/2fa`, { method: "DELETE" });
  },
};

// ---------------------------------------------------------------- Kanalseiten (f-web)
// Eigene Hörerseite je Kanal. Dieselben Seitenrouten wie beim Radio, nur mit
// channel_id; dazu Aussehen/Schalter der Kanalseite und die Bereichsliste für
// „Seiten bauen“.

/** Aussehen und Schalter einer Kanalseite. Leere Felder erben vom Radio. */
export interface KanalSeite {
  aktiv: boolean;
  titel: string | null;
  untertitel: string | null;
  logo_url: string | null;
  farben: { primary?: string; secondary?: string; accent?: string; background?: string; text?: string };
  /** Überschreibungen in der Form von site_settings.settings; leer = erben. */
  settings: Record<string, unknown>;
  zurueck_link: boolean;
  eigene_adresse: string | null;
  updated_at: string | null;
}

/** Was die angemeldete Person an diesem Kanal darf (Bequemlichkeit, der Server entscheidet). */
export interface KanalRechte {
  /** Seiten und Aussehen pflegen: site.write im Kanal oder Admin. */
  pflegen: boolean;
  /** Einschalten und eigene Adresse: Admin oder channel.manage im Kanal. */
  verwalten: boolean;
}

export interface SeitenBereich {
  slug: string;
  name: string;
  channel_id: string;
  aktiv: boolean;
  rechte: KanalRechte;
}

export const kanalSeiteApi = {
  get(slug: string): Promise<{ site: KanalSeite; pages: SitePageSummary[]; public_url: string; rechte?: KanalRechte }> {
    return request(`/channels/${encodeURIComponent(slug)}/site`);
  },
  save(slug: string, input: Omit<KanalSeite, "updated_at">): Promise<unknown> {
    return request(`/channels/${encodeURIComponent(slug)}/site`, { method: "PUT", body: JSON.stringify(input) });
  },
  /**
   * Kanäle, deren Seiten die Person pflegen oder verwalten darf (Admin: alle).
   * Rückfall, solange die Route fehlt (404): Kanalliste und je Kanal die
   * Kanalseite fragen – ein paar Anfragen mehr, aber dasselbe Ergebnis.
   */
  async bereiche(): Promise<SeitenBereich[]> {
    try {
      const res = await request<{ bereiche: SeitenBereich[] }>("/site/bereiche");
      return res.bereiche;
    } catch (err) {
      if (!(err instanceof Error) || !err.message.startsWith("404")) throw err;
    }
    const kanaele = await request<Array<{ id: string; slug: string; name: string }>>("/channels");
    const liste = await Promise.all(kanaele.map(async (k): Promise<SeitenBereich | null> => {
      try {
        const res = await kanalSeiteApi.get(k.slug);
        const rechte = res.rechte ?? { pflegen: false, verwalten: false };
        return { slug: k.slug, name: k.name, channel_id: k.id, aktiv: res.site.aktiv, rechte };
      } catch {
        return null;
      }
    }));
    return liste.filter((b): b is SeitenBereich => b !== null && (b.rechte.pflegen || b.rechte.verwalten));
  },
};

/** Die kanalfähigen Seitenrouten. channelId = null ist die Radio-Hauptseite, genau wie siteApi. */
export const bereichSiteApi = {
  overview(channelId: string | null): Promise<SiteOverview> {
    return request(channelId ? `/site?channel_id=${encodeURIComponent(channelId)}` : "/site");
  },
  createPage(input: SitePageInput, channelId: string | null): Promise<SitePageSummary> {
    return request("/site/pages", { method: "POST", body: JSON.stringify(channelId ? { ...input, channel_id: channelId } : input) });
  },
  reorderPages(ids: string[], channelId: string | null): Promise<{ ok: boolean }> {
    return request("/site/pages/order", { method: "POST", body: JSON.stringify(channelId ? { ids, channel_id: channelId } : { ids }) });
  },
  applyTemplate(key: string, replace: boolean, look: boolean, channelId: string | null): Promise<{ ok: boolean; pages: string[]; aussehen: boolean; message: string }> {
    const q = channelId ? `?channel_id=${encodeURIComponent(channelId)}` : "";
    return request(`/site/templates/${key}${q}`, { method: "POST", body: JSON.stringify({ replace, look }) });
  },
};
// ---------------------------------------------------------------- Ende Kanalseiten (f-web)

// ---------------------------------------------------------------- Hosting: Betrieb, Tarif, Support (h-ui)

/** Verständliche Namen der Grenzen – für Fehlersätze, „Mein Tarif“ und die Betreiberseiten. */
export const GRENZEN_NAMEN: Record<keyof Grenzen, string> = {
  kanaele: "Kanäle",
  mediathek_gb: "Speicher Mediathek",
  bilder_mb: "Speicher Bilder",
  hoerer_gleichzeitig: "Hörer gleichzeitig je Kanal",
  bitrate_max_kbps: "Höchste Bitrate",
  studio_geraete: "Gekoppelte Studio-Geräte",
  kanalseiten: "Eigene Kanalseiten",
  eigene_domain: "Eigene Adresse (Domain)",
  mitschnitt: "Mitschnitt",
};

/** Fehler „Grenze des Tarifs erreicht“ (409 limit_reached) – message ist schon ein deutscher Satz. */
export class GrenzeErreicht extends Error {
  constructor(message: string, readonly grenze: string, readonly erlaubt: unknown, readonly aktuell: unknown) {
    super(message);
    this.name = "GrenzeErreicht";
  }
}

/**
 * Aus einer Fehlerantwort den Error für die Seiten machen. Alles bleibt wie
 * bisher („<status> <body>“, das parsen errorText/kontoFehler), nur die
 * Tarifgrenze wird zum fertigen Satz – so zeigt jede Seite, die `err.message`
 * oder `errorText(err)` anzeigt, von selbst etwas Verständliches. Zusätzlich
 * geht ein Ereignis an die Oberfläche (Layout zeigt einen Hinweis mit Weg zu
 * „Mein Tarif“), damit auch Seiten, die Fehler verschlucken, nichts verstecken.
 */
export function fehlerAusAntwort(status: number, text: string): Error {
  if (status === 409) {
    try {
      const b = JSON.parse(text) as { error?: string; grenze?: string; erlaubt?: unknown; aktuell?: unknown; message?: string };
      if (b.error === "limit_reached") {
        const name = GRENZEN_NAMEN[b.grenze as keyof Grenzen] ?? b.grenze ?? "eine Grenze";
        const satz = b.message ?? `Das geht mit dem Tarif dieses Radios nicht: Die Grenze „${name}“ ist erreicht.`;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("wrs:grenze", { detail: { grenze: b.grenze, message: satz } }));
        }
        return new GrenzeErreicht(satz, b.grenze ?? "", b.erlaubt, b.aktuell);
      }
    } catch {
      /* kein JSON */
    }
  }
  return new Error(`${status} ${text}`);
}

// --- Support-Zugriff: die zweite Sitzung im Tab ---------------------------
//
// Der Betreiber bekommt beim Start ein kurzlebiges Token (tenant_admin des
// Radios). Es liegt in sessionStorage, NICHT in localStorage: Es gilt nur in
// diesem Tab, das eigene Token bleibt, wo es ist. Beenden = Eintrag weg,
// Server Bescheid geben (mit dem eigenen Token), neu laden. Läuft es ab oder
// antwortet der Server mit 401, fällt der Tab von selbst in die eigene Sitzung
// zurück – hängen bleiben kann man so nicht.

const SUPPORT_KEY = "wbr_support";

export interface SupportSitzung {
  token: string;
  zugriff_id: string;
  radio_id: string;
  radio_name: string;
  /** ISO-Zeitpunkt, zu dem das Token abläuft. */
  ende: string;
  art: "freigegeben" | "notfall";
}

export function supportSitzung(): SupportSitzung | null {
  try {
    const raw = sessionStorage.getItem(SUPPORT_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as SupportSitzung;
    // Abgelaufen: nicht mehr benutzen (das Band lädt dann die eigene Sitzung).
    if (!s.token || Date.parse(s.ende) <= Date.now()) {
      sessionStorage.removeItem(SUPPORT_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

/** Support-Sitzung in diesem Tab verlassen und neu laden – ohne den Server zu fragen. */
export function supportVerlassen(ziel: string): void {
  try { sessionStorage.removeItem(SUPPORT_KEY); } catch { /* egal */ }
  if (typeof window !== "undefined") window.location.assign(ziel);
}

/** Beenden-Knopf: Server Bescheid geben (mit dem eigenen Token), dann zurück. */
export async function supportBeenden(): Promise<void> {
  const s = supportSitzung();
  try { sessionStorage.removeItem(SUPPORT_KEY); } catch { /* egal */ }
  if (s) {
    await request(`/betrieb/support/${encodeURIComponent(s.zugriff_id)}/beenden`, { method: "POST", body: JSON.stringify({}) }).catch(() => undefined);
  }
  window.location.assign(s ? `/betrieb/radios/${encodeURIComponent(s.radio_id)}` : "/betrieb/radios");
}

function supportBeginnen(s: SupportSitzung): void {
  sessionStorage.setItem(SUPPORT_KEY, JSON.stringify(s));
  window.location.assign("/dashboard");
}

/** Rohnutzung der API in die Anzeigeform bringen (Kurznamen wie die Grenzschlüssel). */
function nutzungAnzeige(n: Record<string, unknown> | undefined): Nutzung {
  const z = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);
  const r = n ?? {};
  return {
    ...(r as Nutzung),
    kanalseiten: z(r.kanalseiten) ?? z(r.kanalseiten_aktiv),
    eigene_domain: z(r.eigene_domain) ?? z(r.eigene_domains),
    mitschnitt: z(r.mitschnitt) ?? z(r.mitschnitt_aktiv),
    hoerer_spitze: z(r.hoerer_spitze) ?? z(r.hoerer_jetzt_max_kanal),
  };
}

/** Betreiber-Konsole: nur super_admin (der Server prüft). */
export const betriebApi = {
  radios(): Promise<BetriebRadio[]> {
    return request<{ radios: BetriebRadio[] } | BetriebRadio[]>("/betrieb/radios").then((r) => (Array.isArray(r) ? r : r.radios));
  },
  /** Ein Radio mit Grenzen, Nutzung, Leitung und Support – flach zusammengelegt. */
  async radio(id: string): Promise<BetriebRadioDetail> {
    const r = await request<{
      radio: Omit<BetriebRadioDetail, "grenzen" | "nutzung" | "leitung" | "freigabe" | "zugriffe">;
      grenzen: Grenzen;
      nutzung: Record<string, unknown>;
      leitung: BetriebRadioDetail["leitung"];
      support?: { freigabe?: SupportFreigabe | null; zugriffe?: SupportZugriff[] };
    }>(`/betrieb/radios/${encodeURIComponent(id)}`);
    return {
      ...r.radio,
      grenzen: r.grenzen,
      nutzung: nutzungAnzeige(r.nutzung),
      leitung: r.leitung ?? [],
      freigabe: r.support?.freigabe ?? null,
      zugriffe: r.support?.zugriffe ?? [],
    };
  },
  anlegen(body: { name: string; slug: string; tarif: string; admin_email: string; admin_name: string; kontakt_email?: string }): Promise<{
    radio: BetriebRadio | null;
    admin: { id: string; email: string };
    /** verschickt=false: keine Mail (kein SMTP oder Fehler) – dann steht der Link hier. */
    einladung: { verschickt: boolean; link?: string; fehler?: string; gueltig_bis?: string };
  }> {
    return request("/betrieb/radios", { method: "POST", body: JSON.stringify(body) });
  },
  aendern(id: string, body: { tarif?: string; limits_json?: Partial<Grenzen>; kontakt_email?: string | null; notiz?: string | null }): Promise<unknown> {
    return request(`/betrieb/radios/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  sperren(id: string, grund: string): Promise<unknown> {
    return request(`/betrieb/radios/${encodeURIComponent(id)}/sperren`, { method: "POST", body: JSON.stringify({ grund }) });
  },
  entsperren(id: string): Promise<unknown> {
    return request(`/betrieb/radios/${encodeURIComponent(id)}/entsperren`, { method: "POST", body: JSON.stringify({}) });
  },
  loeschen(id: string, grund: string, bestaetigung: string): Promise<{ loeschen_ab?: string }> {
    return request(`/betrieb/radios/${encodeURIComponent(id)}/loeschen`, { method: "POST", body: JSON.stringify({ grund, bestaetigung }) });
  },
  loeschenAbbrechen(id: string): Promise<unknown> {
    return request(`/betrieb/radios/${encodeURIComponent(id)}/loeschen-abbrechen`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Startet den Support-Zugriff und wechselt diesen Tab in die Sitzung des Radios. */
  async supportStarten(radio: { id: string; name: string }, grund: string, art: "freigegeben" | "notfall"): Promise<void> {
    const r = await request<Record<string, unknown>>(`/betrieb/radios/${encodeURIComponent(radio.id)}/support`, {
      method: "POST", body: JSON.stringify({ grund, art }),
    });
    const z = (r.zugriff ?? {}) as Record<string, unknown>;
    const token = String(r.access_token ?? "");
    const zugriffId = String(z.id ?? "");
    const ende = String(z.ende ?? (typeof r.expires_in === "number" ? new Date(Date.now() + r.expires_in * 1000).toISOString() : new Date(Date.now() + 30 * 60_000).toISOString()));
    if (!token || !zugriffId) throw new Error("Der Server hat kein Zugriffstoken geschickt.");
    supportBeginnen({ token, zugriff_id: zugriffId, radio_id: radio.id, radio_name: radio.name, ende, art: (r.art ?? z.art ?? art) as SupportSitzung["art"] });
  },
  /** Einen laufenden Zugriff beenden (z. B. aus einem anderen Tab). */
  supportZugriffBeenden(zugriffId: string): Promise<{ ok: true }> {
    return request(`/betrieb/support/${encodeURIComponent(zugriffId)}/beenden`, { method: "POST", body: JSON.stringify({}) });
  },
  tarife(): Promise<Tarif[]> {
    return request<{ tarife: Tarif[] } | Tarif[]>("/betrieb/tarife").then((r) => (Array.isArray(r) ? r : r.tarife));
  },
  tarifAnlegen(t: Omit<Tarif, "radios">): Promise<unknown> {
    return request("/betrieb/tarife", { method: "POST", body: JSON.stringify(t) });
  },
  tarifAendern(key: string, t: Omit<Tarif, "radios" | "key">): Promise<unknown> {
    return request(`/betrieb/tarife/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify(t) });
  },
  tarifLoeschen(key: string): Promise<unknown> {
    return request(`/betrieb/tarife/${encodeURIComponent(key)}`, { method: "DELETE" });
  },
  nutzung(monat: string): Promise<BetriebNutzungZeile[]> {
    return request<{ radios: BetriebNutzungZeile[] } | BetriebNutzungZeile[]>(`/betrieb/nutzung?monat=${encodeURIComponent(monat)}`)
      .then((r) => (Array.isArray(r) ? r : r.radios));
  },
  /** CSV mit Anmeldung laden und im Browser speichern. */
  async nutzungCsv(monat: string): Promise<void> {
    const res = await fetch(`/api/betrieb/nutzung.csv?monat=${encodeURIComponent(monat)}`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? `nutzung-${monat}.csv`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

/** Radio-Seite: eigener Tarif und Support-Freigabe. */
export const tarifApi = {
  meinTarif(): Promise<MeinTarif> {
    return request("/tenant/tarif");
  },
  freigabe(): Promise<SupportFreigabe> {
    return request("/tenant/support-freigabe");
  },
  /** bis = null widerruft die Freigabe. */
  freigabeSetzen(bis: string | null): Promise<SupportFreigabe> {
    return request("/tenant/support-freigabe", { method: "PUT", body: JSON.stringify({ bis }) });
  },
  /** Die Radioleitung beendet einen laufenden Support-Zugriff selbst. */
  zugriffBeenden(id: string): Promise<{ ok: true }> {
    return request(`/tenant/support-zugriffe/${encodeURIComponent(id)}/beenden`, { method: "POST", body: JSON.stringify({}) });
  },
  zugriffe(): Promise<SupportZugriff[]> {
    return request<{ zugriffe: SupportZugriff[] } | SupportZugriff[]>("/tenant/support-zugriffe").then((r) => (Array.isArray(r) ? r : r.zugriffe));
  },
};
// ---------------------------------------------------------------- Ende Hosting (h-ui)

// ---------------------------------------------------------------- Eigene Domains (h-domain)
export const domainApi = {
  liste(): Promise<DomainListe> {
    return request("/tenant/domains");
  },
  anlegen(host: string, mit_www: boolean): Promise<{ domain: RadioDomain }> {
    return request("/tenant/domains", { method: "POST", body: JSON.stringify({ host, mit_www }) });
  },
  aendern(host: string, patch: { primaer?: true; mit_www?: boolean }): Promise<{ domain: RadioDomain }> {
    return request(`/tenant/domains/${encodeURIComponent(host)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  entfernen(host: string): Promise<void> {
    return request(`/tenant/domains/${encodeURIComponent(host)}`, { method: "DELETE" });
  },
  pruefen(host: string): Promise<{ domain: RadioDomain }> {
    return request(`/tenant/domains/${encodeURIComponent(host)}/pruefen`, { method: "POST" });
  },
  /** Betreiber: die Domains eines Radios. */
  vonRadio(tenantId: string): Promise<RadioDomain[]> {
    return request<{ domains: RadioDomain[] }>(`/betrieb/radios/${encodeURIComponent(tenantId)}/domains`).then((r) => r.domains);
  },
};
// ---------------------------------------------------------------- Ende Eigene Domains (h-domain)

// ---------------------------------------------------------------- Umzug, Datenauskunft, Löschnachweise (h-umzug)

/**
 * Große Archive lädt der Browser direkt – über einen kurzlebigen Link (5 min),
 * den die API ausstellt. So landet ein Archiv mit vielen Gigabyte nie im
 * Speicher der Seite.
 */
async function archivOeffnen(linkPfad: string): Promise<void> {
  const { url } = await request<{ url: string }>(linkPfad, { method: "POST", body: JSON.stringify({}) });
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.click();
}

export interface UploadFortschritt {
  geladen: number;
  gesamt: number;
}

export const umzugApi = {
  exportStarten(radioId: string, mitschnitte: boolean): Promise<{ auftrag: UmzugAuftrag }> {
    return request(`/betrieb/radios/${encodeURIComponent(radioId)}/export`, { method: "POST", body: JSON.stringify({ mitschnitte }) });
  },
  exporte(radioId?: string): Promise<{ exporte: UmzugAuftrag[]; aufbewahrung_tage: number }> {
    return request(`/betrieb/exporte${radioId ? `?radio=${encodeURIComponent(radioId)}` : ""}`);
  },
  exportLoeschen(id: string): Promise<{ ok: true }> {
    return request(`/betrieb/exporte/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  exportHerunterladen(id: string): Promise<void> {
    return archivOeffnen(`/betrieb/exporte/${encodeURIComponent(id)}/link`);
  },
  importe(): Promise<{ importe: UmzugAuftrag[] }> {
    return request("/betrieb/importe");
  },
  import(id: string): Promise<{ auftrag: UmzugAuftrag }> {
    return request(`/betrieb/importe/${encodeURIComponent(id)}`);
  },
  importFortsetzen(id: string, slug: string): Promise<{ auftrag: UmzugAuftrag }> {
    return request(`/betrieb/importe/${encodeURIComponent(id)}/fortsetzen`, { method: "POST", body: JSON.stringify({ slug }) });
  },
  importVerwerfen(id: string): Promise<{ ok: true }> {
    return request(`/betrieb/importe/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  /**
   * Archiv hochladen – als Strom mit Fortschritt (XMLHttpRequest kann das, fetch nicht).
   * Vorher eine kleine Anfrage: Sie erneuert bei Bedarf die Anmeldung, damit ein
   * großer Upload nicht erst am Ende an einem abgelaufenen Token scheitert.
   */
  async importHochladen(datei: File, slug: string | null, beiFortschritt: (f: UploadFortschritt) => void): Promise<{ auftrag: UmzugAuftrag }> {
    await request("/betrieb/importe");
    const token = getAccessToken();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `/api/betrieb/radios/import${slug ? `?slug=${encodeURIComponent(slug)}` : ""}`);
      xhr.withCredentials = true;
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("Content-Type", "application/gzip");
      xhr.setRequestHeader("X-Dateiname", encodeURIComponent(datei.name).slice(0, 200));
      xhr.upload.onprogress = (e) => beiFortschritt({ geladen: e.loaded, gesamt: e.lengthComputable ? e.total : datei.size });
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as { auftrag: UmzugAuftrag });
          } catch {
            reject(new Error("Unerwartete Antwort des Servers."));
          }
        } else reject(fehlerAusAntwort(xhr.status, xhr.responseText));
      };
      xhr.onerror = () => reject(new Error("Der Upload ist abgebrochen (Verbindung unterbrochen)."));
      xhr.onabort = () => reject(new Error("Upload abgebrochen."));
      xhr.send(datei);
    });
  },
  loeschnachweise(): Promise<{ nachweise: Loeschnachweis[]; loeschen_aktiv: boolean }> {
    return request("/betrieb/loeschnachweise");
  },
  // Radio-Seite: Datenauskunft
  datenexportStarten(mitschnitte: boolean): Promise<{ auftrag: UmzugAuftrag }> {
    return request("/tenant/datenexport", { method: "POST", body: JSON.stringify({ mitschnitte }) });
  },
  datenexporte(): Promise<{ exporte: UmzugAuftrag[]; aufbewahrung_tage: number }> {
    return request("/tenant/datenexporte");
  },
  datenexportHerunterladen(id: string): Promise<void> {
    return archivOeffnen(`/tenant/datenexporte/${encodeURIComponent(id)}/link`);
  },
};
// ---------------------------------------------------------------- Ende Umzug (h-umzug)
